import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Listbox } from '@headlessui/react';
import { QualifyingCell, QualifyingStageChip } from '../../components/screenings/qualifyingAgentStatus';
import QualifyingAgentBoundary from '../../components/screenings/QualifyingAgentBoundary';
import {
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CheckIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
  PhoneIcon,
  PhoneArrowUpRightIcon,
  StarIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
  VideoCameraIcon,
  XMarkIcon,
  ArrowUturnLeftIcon,
  ArrowRightCircleIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import {
  Tooltip as UITooltip,
  TooltipContent as UITooltipContent,
  TooltipProvider as UITooltipProvider,
  TooltipTrigger as UITooltipTrigger,
} from 'src/components/ui/ui/tooltip';
import {
  getCombinedScreeningProctoringBadge,
  getInterviewProctoringRiskBadgeFromScore,
  getRiskBadgeFromScore,
  getTotalRiskPointsFromDetails,
} from '../../components/results/proctoringUtils';
import {
  isPersonalityResult,
  formatPersonalityChip,
  describePersonality,
} from '../../components/results/personalityProfile';
import {
  EXPERIENCE_BUCKETS,
  AGE_BUCKETS,
  CTC_BUCKETS,
  PROCTORING_EYE_GAZE_CRITERIA,
  resolveCandidateDisplayName,
} from './screeningConstants';
import { ScoreFilterDropdown } from './ScoreFilterDropdown';
import MetaFilterDropdown from './MetaFilterDropdown';
import { getCandidatesTableLayout } from './candidatesTableLayout';
import DocumentStatusBadge from '../../components/documents/DocumentStatusBadge';
import { calculateWeightedOverallScore } from '../../utils/scoreCalculator';
import { Linkedin, Github, Globe, Briefcase, PenTool, Link as LinkIcon } from 'lucide-react';
import AnimatedModal from '../../components/ui/AnimatedModal';
import CandidateNotesModal from '../../components/screenings/CandidateNotesModal';
import { ApplicationFormCell } from '../../components/screenings/ApplicationFormModal';
import { useAuth } from '../../contexts/AuthContext';
import { getVisibleAssessmentModuleColumns } from './assessmentModuleColumns';
import { isResumeExtractionFailed } from './resumeCellState';
import { resolveGoColorsDesignation } from './goColorsDesignation';
import { filterStoresByFormat } from '../../utils/storeFormat';
import {
  AssessmentModuleColumnHeaders,
  AssessmentModuleScoreCells,
} from './AssessmentModuleTableCells';

const DOWNLOADED_CHIP = {
  true: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
  false: 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100',
};

// Mounted only while open; portalled because the table's scroll container clips absolute menus.
function PayrollDownloadedOptions({ buttonRef, value }) {
  const [, reposition] = React.useReducer((n) => n + 1, 0);
  React.useEffect(() => {
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, []);
  const rect = buttonRef.current?.getBoundingClientRect();
  if (!rect) return null;
  return createPortal(
    <Listbox.Options
      static
      className="fixed z-[9999] w-36 -translate-x-1/2 rounded-xl border border-gray-100 bg-white p-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
      style={{ top: rect.bottom + 6, left: rect.left + rect.width / 2 }}
    >
      {[true, false].map((opt) => (
        <Listbox.Option
          key={String(opt)}
          value={opt}
          className={({ active }) =>
            `flex cursor-pointer items-center justify-between rounded-lg px-2.5 py-2 text-xs transition-colors ${active ? 'bg-gray-50' : ''}`
          }
        >
          <span className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${opt ? 'bg-emerald-500' : 'bg-gray-400'}`} />
            <span className="font-medium text-gray-800">{opt ? 'Yes' : 'No'}</span>
            <span className="text-gray-400">{opt ? 'Downloaded' : 'Not yet'}</span>
          </span>
          {opt === value && <CheckIcon className="h-3.5 w-3.5 text-[#007BFF]" />}
        </Listbox.Option>
      ))}
    </Listbox.Options>,
    document.body
  );
}

function PayrollDownloadedSelect({ downloaded, title, onChange }) {
  const buttonRef = React.useRef(null);
  return (
    <Listbox value={downloaded} onChange={(v) => v !== downloaded && onChange(v)}>
      {({ open }) => (
        <>
          <Listbox.Button
            ref={buttonRef}
            data-testid="payroll-downloaded-select"
            title={title}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#007BFF]/20 ${DOWNLOADED_CHIP[downloaded]}`}
          >
            {downloaded && <CheckCircleIcon className="h-3.5 w-3.5 shrink-0" />}
            {downloaded ? 'Yes' : 'No'}
            <ChevronDownIcon className={`h-3 w-3 shrink-0 opacity-60 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
          </Listbox.Button>
          {open && <PayrollDownloadedOptions buttonRef={buttonRef} value={downloaded} />}
        </>
      )}
    </Listbox>
  );
}

function TableStoreDropdown({ value, stores, onChange }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const containerRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const [dropdownPos, setDropdownPos] = React.useState({});

  const updatePosition = React.useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left + rect.width / 2,
      });
    }
  }, []);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current && !containerRef.current.contains(event.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    }
    
    function handleScroll(e) {
      if (isOpen && (!dropdownRef.current || !dropdownRef.current.contains(e.target))) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updatePosition);

    if (isOpen) {
      updatePosition();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  const filtered = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter(s =>
      String(s.storeId || '').toLowerCase().includes(q) ||
      String(s.name || '').toLowerCase().includes(q) ||
      String(s.city || '').toLowerCase().includes(q)
    );
  }, [stores, searchQuery]);

  return (
    <div ref={containerRef} className="relative w-[75px] mx-auto text-left">
      <div className="relative">
        <input
          type="text"
          placeholder="Code..."
          value={isOpen ? searchQuery : (value || '')}
          onChange={(e) => {
            if (!isOpen) setIsOpen(true);
            setSearchQuery(e.target.value);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchQuery('');
          }}
          className="w-full rounded-lg border border-slate-200 pl-2 pr-6 py-1.5 text-[11px] focus:border-indigo-500 focus:outline-none bg-white text-center font-semibold text-indigo-700 hover:border-slate-300 transition-colors cursor-pointer"
          style={{ color: '#4F46E5' }}
        />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
          <ChevronDownIcon className="h-3 w-3 text-slate-400" aria-hidden="true" />
        </div>
      </div>
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed z-[9999] max-h-48 w-56 overflow-y-auto rounded-lg border border-slate-100 bg-white py-1 shadow-lg ring-1 ring-black/5 -translate-x-1/2"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        >
          <button
            type="button"
            onClick={() => {
              onChange('');
              setIsOpen(false);
            }}
            className={`w-full text-left px-3 py-2 text-xs hover:bg-rose-50 hover:text-rose-600 transition-colors border-b border-slate-100 ${
              !value ? 'bg-indigo-50/50 text-indigo-700 font-semibold' : 'text-slate-500'
            }`}
          >
            None (Clear Code)
          </button>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500 text-center">
              No stores found
            </div>
          ) : (
            filtered.map((store) => (
              <button
                key={store.storeId}
                type="button"
                onClick={() => {
                  onChange(store.storeId);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 hover:text-indigo-600 transition-colors flex flex-col ${
                  value === store.storeId ? 'bg-indigo-50/50 text-indigo-700 font-semibold' : 'text-slate-700'
                }`}
              >
                <span className="font-bold">{store.storeId}</span>
                <span className="text-[10px] text-slate-400 block truncate">{store.name} {store.city ? `(${store.city})` : ''}</span>
              </button>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * Platform-level default column order for the candidates table.
 * This is the hardcoded fallback used when no org-level or user-level
 * column order has been configured.
 *
 * NOTE: 'decision' appears first so it is positioned prominently in the
 * Selected and Rejected tabs (where it is visible). It is not rendered
 * in the Candidates tab.
 * NOTE: 'notes' appears LAST — it is an action/write column and belongs
 * at the end of the column list.
 */
export const META_COLUMN_DEFAULT_ORDER = [
  'status',
  'decision',
  'overallScore', 'resumeScore', 'assessmentScore', 'interviewScore',
  'shortlistedDate', 'rejectedDate', 'salaryDetails', 'downloaded', 'completedAt', 'appliedDate',
  'proctoring', 'duplicates', 'documents', 'applicationForm', 'qualifyingCall', 'transcriptsAvailable',
  'screening',
  'companyName', 'currentCtc', 'noticePeriod', 'designation', 'experience',
  'experienceYears', 'latestEducation', 'industry', 'location', 'skills', 'age', 'gender',
  'storeId', 'storeName', 'storeRegion', 'storeCity', 'storeState', 'storeCategory', 'armName', 'armEmail',
  'cityCode', 'cityCity', 'cityState', 'cityRegion',
  'email', 'phone', 'social',
  'candidateDetails',
  'notes', // always last — action/write column
];

/**
 * Places a column that the saved order has never heard of.
 *
 * A recruiter's saved column order is a snapshot of the columns that existed the day they
 * saved it. Ship a new column afterwards and it is in no saved order anywhere, so every
 * recruiter who has ever touched the Columns dialog is the last to see it — which is the
 * usual way a new column silently fails to appear in a table with persisted layout
 * preferences. The saved order is therefore treated as a preference about the columns it
 * names, never as the list of columns that exist.
 *
 * Appending is not good enough. This table scrolls horizontally on a wide screening, so a
 * column dropped at the far right is off-screen behind the store and city columns, and
 * reads to the person looking at it exactly like a column that was never built. Instead the
 * new column lands where the platform default says it belongs: directly after the nearest
 * column ahead of it in META_COLUMN_DEFAULT_ORDER that is already on screen. That keeps the
 * recruiter's own arrangement intact and still puts "Qualifying call" next to the other
 * per-candidate stage columns rather than past the end of their layout.
 *
 * Mutates `ordered` in place, matching the surrounding splice-based code.
 */
/** Whether the "Transcripts available" column exists for this org + screening. */
export function isTranscriptsColumnAvailable(user, screeningMeta) {
  return user?.features?.transcripts_confirmation === true && screeningMeta?.hasResumeScoring !== false;
}

/** The candidate's answer to the transcripts checkbox. Unticked (or not stored) reads as No. */
export function getTranscriptsAvailableLabel(candidate) {
  return candidate?.customFields?.transcripts_available === 'Yes' ? 'Yes' : 'No';
}

export function insertColumnAtDefaultPosition(ordered, id, defaultOrder = META_COLUMN_DEFAULT_ORDER) {
  if (ordered.includes(id)) return ordered;

  const defaultIndex = defaultOrder.indexOf(id);
  // A column with no platform default position (an org-specific or dynamically generated one)
  // has no anchor to reason from, so the end is the only honest place for it.
  if (defaultIndex === -1) {
    ordered.push(id);
    return ordered;
  }

  for (let i = defaultIndex - 1; i >= 0; i -= 1) {
    const anchor = ordered.indexOf(defaultOrder[i]);
    if (anchor !== -1) {
      ordered.splice(anchor + 1, 0, id);
      return ordered;
    }
  }

  // Nothing that precedes it by default is on screen, so by definition it comes first.
  ordered.unshift(id);
  return ordered;
}

// Helper to check for previous Burger King experience
function hasBurgerKingExperience(candidate) {
  const fromSrt = candidate?.structuredResumeText?.workExperience;
  const top = candidate?.workExperience;
  const we = (Array.isArray(fromSrt) && fromSrt.length > 0) ? fromSrt : ((Array.isArray(top) && top.length > 0) ? top : []);
  return we.some((exp) => exp.companyName && exp.companyName.toLowerCase().includes('burger king'));
}

export default function CandidatesTable(props) {
  const { user } = useAuth();
  const isBurgerKingClient = user?.orgSlug === 'b-pia1dr';
  const isSamtaGroup = String(user?.orgId) === '6a3b9faeb52a49d66cc5f63e';
  const { rows, options = {} } = props;
  const [displayCount, setDisplayCount] = useState(50);

  useEffect(() => {
    setDisplayCount(50);
  }, [rows?.length]);
  const screeningMeta = props.screeningMeta;
  const scoreFilters = props.scoreFilters;
  const setScoreFilters = props.setScoreFilters;
  const metaFilters = props.metaFilters;
  const setMetaFilters = props.setMetaFilters;
  const openScoreFilterId = props.openScoreFilterId;
  const setOpenScoreFilterId = props.setOpenScoreFilterId;
  const openMetaFilterId = props.openMetaFilterId;
  const setOpenMetaFilterId = props.setOpenMetaFilterId;
  const openActionsCandidate = props.openActionsCandidate;
  const setOpenActionsCandidate = props.setOpenActionsCandidate;
  const actionMenuPosition = props.actionMenuPosition;
  const setActionMenuPosition = props.setActionMenuPosition;
  const parseExperienceYearsForFilter = props.parseExperienceYearsForFilter;
  const parseAgeYearsForFilter = props.parseAgeYearsForFilter;
  const parseCurrentCtcLacsForFilter = props.parseCurrentCtcLacsForFilter;
  const openCandidateResumeBreakdown = props.openCandidateResumeBreakdown;
  const openInterviewSessionForCandidate = props.openInterviewSessionForCandidate;
  const openWhatsAppSessionForCandidate = props.openWhatsAppSessionForCandidate;
  const openQualifyingSessionForCandidate = props.openQualifyingSessionForCandidate;
  const openQualifyingAnswersForCandidate = props.openQualifyingAnswersForCandidate;
  const handleRemoveFromShortlistRow = props.handleRemoveFromShortlistRow;
  const handleRestoreFromRejectedRow = props.handleRestoreFromRejectedRow;
  const handleRestoreFromDroppedRow = props.handleRestoreFromDroppedRow;
  const setCandidateToReject = props.setCandidateToReject;
  const setShowRejectConfirmModal = props.setShowRejectConfirmModal;
  const handleInlineViewAssessmentScore = props.handleInlineViewAssessmentScore;
  const handleInlineViewAssessmentProctoring = props.handleInlineViewAssessmentProctoring;
  const setCandidateToDelete = props.setCandidateToDelete;
  const setCandidateToResend = props.setCandidateToResend;
  const setCandidateToShortlist = props.setCandidateToShortlist;
  const setShowResendConfirmModal = props.setShowResendConfirmModal;
  const setShowShortlistConfirmModal = props.setShowShortlistConfirmModal;
  const documentStatusMap = props.documentStatusMap; // null means loading
  const onViewDocumentsCandidate = props.onViewDocumentsCandidate;
  const columnVisibility = options?.columnVisibility || {};
  const shortlistDecisions = props.shortlistDecisions || {};
  const setShortlistDecisions = props.setShortlistDecisions || (() => { });
  const shortlistNotes = props.shortlistNotes || {};
  const setShortlistNotes = props.setShortlistNotes || (() => { });
  const patchCandidateShortlistField = props.patchCandidateShortlistField || (() => { });
  const screeningId = props.screeningId || null;
  const stores = props.stores || [];
  // Snapshot-first store resolution — mirrors getResolvedStoreDetails in
  // server/modules/reports/reports.route.js. A candidate's own storeDetails
  // (captured once, when they were linked to a store) must win over the live
  // store list here too, otherwise editing a store's name/ARM/region later
  // retroactively changes how already-linked candidates display in this table
  // — the exact "no cascade" rule store.controller.js enforces on write is
  // pointless if the read path re-introduces it. Live `stores` is only a
  // fallback for candidates that predate storeDetails capture. Built as a
  // Map (not a per-cell .find()) so every store-derived cell in every row is O(1).
  const storeById = React.useMemo(() => {
    const map = new Map();
    stores.forEach((s) => {
      if (s?.storeId) map.set(String(s.storeId), s);
    });
    return map;
  }, [stores]);
  // The store picker offers only stores of this job's format; storeById above keeps the
  // full list so a candidate already on another store still displays correctly.
  const storeOptions = React.useMemo(
    () => filterStoresByFormat(stores, screeningMeta?.storeFormat),
    [stores, screeningMeta?.storeFormat]
  );
  const resolveCandidateStore = (c) => {
    if (c.storeDetails && Object.keys(c.storeDetails).length > 0) {
      return c.storeDetails;
    }
    return storeById.get(String(c.storeId)) || null;
  };
  // Org custom-registration-field definitions, which drive the `custom_*` columns.
  // Normally from useAuth; the public hiring-manager review page has no logged-in
  // user and forwards them as a prop instead. Without this the reviewer lost every
  // custom column even though `candidate.customFields` was already in the payload.
  const registrationFields = props.registrationFields || user?.registrationFields || [];
  const notesShareCode = props.notesShareCode || null;
  const notesReviewerName = props.notesReviewerName || '';
  const onMarkCandidateViewed = props.onMarkCandidateViewed;
  const onMarkCandidateUnviewed = props.onMarkCandidateUnviewed;
  const handleMarkDropped = props.handleMarkDropped;
  const onDownloadCandidateReport = props.onDownloadCandidateReport;
  const onReviewCandidateWithHiringManager = props.onReviewCandidateWithHiringManager;
  const isGoColors = props.isGoColors;
  const onMoveCandidateJobPost = props.onMoveCandidateJobPost;
  const onUndoMoveCandidateJobPost = props.onUndoMoveCandidateJobPost;
  const onCallCandidate = props.onCallCandidate;
  const onRecallQualifyingCall = props.onRecallQualifyingCall;
  const weightsConfig = props.weightsConfig;
  const salaryRequestsMap = props.salaryRequestsMap;
  const onOpenSalaryDetailsModal = props.onOpenSalaryDetailsModal;
  const onSetPayrollDownloaded = props.onSetPayrollDownloaded;
  const totalCandidatesProp = props.totalCandidates;
  const hasMoreProp = props.hasMore;
  const onLoadMoreProp = props.onLoadMore;
  const isLoadingMoreProp = props.isLoadingMore;

  const {

    selectedIds = [],

    onSelectionChange,

    showShortlistedDate = true,

    showRejectedDate = true,

    showStatus: showStatusOption = false,

    showAssessment = screeningMeta?.hasAssessment ?? true,

    showInlineEmptyState = false,

    isAISearchActiveForTable = false,

    onClearFilters,

    isShortlistView = false,

    isRejectedView = false,
    isDroppedView = false,
    // Go Colors Selected tab: the payroll "Downloaded" chip.
    showDownloadedColumn = false,

    showDuplicateColumn = false,

    showRecommendedChip = false,

    recommendedChipThreshold = 85,

  } = options;

  const hideRowActions = options?.hideRowActions === true;
  // Explicit flag for the public hiring-manager review page. Prefer this over
  // sniffing which callbacks happen to be missing: anything that only makes
  // sense for a logged-in recruiter (in-app /dashboard deep links, etc.) is
  // suppressed on one named condition instead of a scatter of prop checks.
  const isPublicView = options?.isPublicView === true;
  const [showTelephonyActions, setShowTelephonyActions] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key.toLowerCase() === 'u' || e.code === 'KeyU')) {
        e.preventDefault();
        setShowTelephonyActions((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

const resolvedShowDuplicateColumn = Object.prototype.hasOwnProperty.call(columnVisibility, 'duplicates')
  ? columnVisibility.duplicates === true
  : showDuplicateColumn && rows.some((row) => row?.duplicateSummary?.count > 0);

const showStatus = Object.prototype.hasOwnProperty.call(columnVisibility, 'status')
  ? columnVisibility.status === true
  : (isDroppedView ? false : (isShortlistView || isRejectedView ? false : showStatusOption));

const showDecisionColumn = Object.prototype.hasOwnProperty.call(columnVisibility, 'decision')
  ? columnVisibility.decision === true
  : isShortlistView;

// `notesDefaultVisible` lets a tab opt the Notes column into being shown by default
// (the Rejected tab uses it for orgs with `rejection_reason_required` enabled).
const showNotesColumn = Object.prototype.hasOwnProperty.call(columnVisibility, 'notes')
  ? columnVisibility.notes === true
  : (options?.notesDefaultVisible ?? isShortlistView);

const showDocumentsColumn =
  (options && Object.prototype.hasOwnProperty.call(options, 'showDocumentsColumn'))
    ? options.showDocumentsColumn === true
    : (screeningMeta?.includeDocumentCollection === true || Object.keys(documentStatusMap || {}).length > 0);

const resolvedShowDocumentsColumn =
  screeningMeta?.includeDocumentCollection === true ||
  (Object.prototype.hasOwnProperty.call(columnVisibility, 'documents')
    ? columnVisibility.documents === true
    : showDocumentsColumn);

// The Qualifying Call column replaces the lone eye icon that used to sit in Actions: a
// transcript is content, not an action, and burying it there gave no hint that a call had
// even happened. Shown whenever the screening runs a qualifying stage.
const resolvedShowQualifyingCallColumn =
  Object.prototype.hasOwnProperty.call(columnVisibility, 'qualifyingCall')
    ? columnVisibility.qualifyingCall === true
    : screeningMeta?.includeQualifyingAgent === true;

// Shown whenever the screening has the Application Form step; the cell opens the form + PDF.
const resolvedShowApplicationFormColumn =
  Object.prototype.hasOwnProperty.call(columnVisibility, 'applicationForm')
    ? columnVisibility.applicationForm === true
    : screeningMeta?.includeApplicationForm === true;

// The resume upload step's "transcripts available?" checkbox (transcripts_confirmation flag).
// Only exists where candidates were asked: the org has the flag and the screening has a resume step.
const transcriptsColumnAvailable = isTranscriptsColumnAvailable(user, screeningMeta);
const resolvedShowTranscriptsColumn =
  transcriptsColumnAvailable &&
  (Object.prototype.hasOwnProperty.call(columnVisibility, 'transcriptsAvailable')
    ? columnVisibility.transcriptsAvailable === true
    : true);

const isSelectable = typeof onSelectionChange === 'function';

const hideColumnFilters = options?.hideColumnFilters === true;

const {
  isResumeOnly,
  isAssessmentOnly,
  isInterviewOnly,
  isAssessmentInterviewDual,
  isResumeAssessmentDual,
  isResumeInterviewDual,
  isDualMethodLayout,
  isAllThreeMethods,
  isSimplifiedSingleMode,

  showOverallScoreColumn,
  showResumeScoreColumn,
  showAssessmentScoreColumn,
  showInterviewScoreColumn,
  showShortlistedDateEffective,
  showRejectedDateEffective,
  showProctoringColumn,
  showCandidateMetaColumns,
  showCompanyNameColumn,
  showStoreNameColumn,
  showStoreRegionColumn,
  showStoreCityColumn,
  showStoreStateColumn,
  showStoreCategoryColumn,
  showArmNameColumn,
  showArmEmailColumn,
  showCityCityColumn,
  showCityStateColumn,
  showCityRegionColumn,
  showScreeningColumn,
  showCurrentCtcColumn,
  showNoticePeriodColumn,
  showAgeColumn,
  showGenderColumn,
  showExperienceColumn,
  showLatestEducationColumn,
  showMetaColumnsBlock,
  showDesignationColumn,
  showLocationColumn,
  showIndustryColumn,
  showSkillsColumn,
  showExperienceYearsColumn,
  showEmailColumn,
  showPhoneColumn,
  showSocialColumn,
  showAppliedDateColumn,
  showResumeInterviewExtraColumns,
  showResumeAssessmentExtraColumns,
  showResumeAssessmentExperienceColumn,
  showResumeInterviewExperienceColumn,
  showCompletedAtColumn,
  showSalaryDetailsColumn,
  showCandidateDetailsColumn,
  // storeListEnabled drives the default visibility of the store location columns
  // (see candidatesTableLayout.js). Normally read from useAuth, but the public
  // hiring-manager review page has no logged-in user, so it forwards the org flag
  // through `options` instead — hence the OR rather than a bare useAuth read,
  // which would silently hide these columns on that page.
} = getCandidatesTableLayout(screeningMeta, {
  ...options,
  storeListEnabled: user?.features?.store_list === true || options?.storeListEnabled === true,
});

const visibleAssessmentModuleColumns = React.useMemo(
  () => getVisibleAssessmentModuleColumns(screeningMeta, columnVisibility),
  [screeningMeta, columnVisibility]
);

const handleViewAssessmentModuleScore = React.useCallback(
  (candidate, { moduleIndex } = {}) => {
    if (typeof handleInlineViewAssessmentScore !== 'function') return;
    handleInlineViewAssessmentScore(candidate, { moduleIndex });
  },
  [handleInlineViewAssessmentScore]
);

const [openDecisionDropdownId, setOpenDecisionDropdownId] = useState(null);
const [decisionDropdownPosition, setDecisionDropdownPosition] = useState(null);
const [openNotesModalId, setOpenNotesModalId] = useState(null);
const [openNotesModalName, setOpenNotesModalName] = useState('');
const setDuplicateModalCandidate = props.setDuplicateModalCandidate;
const duplicateModalCandidate = props.duplicateModalCandidate;

const handleNotesModalClose = () => {
  setOpenNotesModalId(null);
  setOpenNotesModalName('');
};

const handleNoteAdded = (candidateId, latestBody) => {
  if (latestBody) {
    setShortlistNotes((prev) => ({ ...prev, [candidateId]: latestBody }));
  }
  props.onCandidateNoteAdded?.(candidateId, latestBody);
};

  const DECISION_OPTIONS = [
    { value: 'offered', label: 'Offered', classes: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200' },
    { value: 'on_hold', label: 'On Hold', classes: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
    { value: 'dropped_out', label: 'Dropped Out', classes: 'bg-gray-50 text-gray-700 ring-1 ring-gray-200' },
  ];

  const getDecisionOption = (value) => DECISION_OPTIONS.find((o) => o.value === value) || null;

  const candidateNameWidthClass = isSimplifiedSingleMode

    ? 'w-[260px]'

    : isResumeAssessmentDual || isResumeInterviewDual

      ? 'w-[220px]'

      : '';

  const ageWidthClass =

    isInterviewOnly || isAssessmentOnly || isResumeAssessmentDual || isResumeInterviewDual ? 'w-[70px]' : '';

  const companyWidthClass = isResumeAssessmentDual || isResumeInterviewDual ? 'w-[200px]' : '';



  const buildBucketOptions = (values, buckets) => {

    const counts = new Map();

    values.forEach((n) => {

      if (n == null || Number.isNaN(Number(n))) return;

      const v = Number(n);

      const bucket = buckets.find((b) => v >= b.min && v <= b.max);

      if (!bucket) return;

      counts.set(bucket.id, (counts.get(bucket.id) || 0) + 1);

    });

    return buckets.filter((b) => counts.has(b.id)).map((b) => ({ value: b.id, label: b.label }));

  };



  const proctoringOptions = [

    { value: 'Compliant', label: 'Compliant' },

    { value: 'Flagged', label: 'Flagged' },

    { value: 'High Risk', label: 'High Risk' },

    { value: 'No data', label: 'No data' },

  ];

  const expOptions = buildBucketOptions(rows.map(parseExperienceYearsForFilter), EXPERIENCE_BUCKETS);

  const ageOptions = buildBucketOptions(rows.map(parseAgeYearsForFilter), AGE_BUCKETS);

  const ctcOptions = buildBucketOptions(rows.map(parseCurrentCtcLacsForFilter), CTC_BUCKETS);

  const noticeOptions = Array.from(

    new Set(

      rows

        .map((c) => (c?.noticePeriod ? String(c.noticePeriod).trim() : ''))

        .filter((s) => s && s.length > 0)

    )

  )

    .sort((a, b) => a.localeCompare(b))

    .map((s) => ({ value: s, label: s }));



  const candidateMetaColumnCount = showMetaColumnsBlock
    ? (showScreeningColumn ? 1 : 0) +
    (showCompanyNameColumn ? 1 : 0) +
    (showDesignationColumn ? 1 : 0) +
    (showLocationColumn ? 1 : 0) +
    (showIndustryColumn ? 1 : 0) +
    (showExperienceColumn ? 1 : 0) +
    (showLatestEducationColumn ? 1 : 0) +
    (showSkillsColumn ? 1 : 0) +
    (showEmailColumn ? 1 : 0) +
    (showPhoneColumn ? 1 : 0) +
    (showSocialColumn ? 1 : 0) +
    (showAppliedDateColumn ? 1 : 0)
    : 0;

  // ── Column ordering ──
  // Default order for the orderable meta columns (inside showMetaColumnsBlock + completedAt).
  const metaColumnVisibilityMap = {
    screening: showScreeningColumn,
    status: showStatus,
    decision: showDecisionColumn,
    notes: showNotesColumn,
    resumeScore: showResumeScoreColumn,
    overallScore: showOverallScoreColumn,
    assessmentScore: showAssessmentScoreColumn,
    interviewScore: showInterviewScoreColumn,
    companyName: showCompanyNameColumn,
    storeId: user?.features?.store_list === true,
    storeName: showStoreNameColumn,
    storeRegion: showStoreRegionColumn,
    storeCity: showStoreCityColumn,
    storeState: showStoreStateColumn,
    storeCategory: showStoreCategoryColumn,
    armName: showArmNameColumn,
    armEmail: showArmEmailColumn,
    cityCity: showCityCityColumn,
    cityState: showCityStateColumn,
    cityRegion: showCityRegionColumn,
    currentCtc: showCurrentCtcColumn,
    noticePeriod: showNoticePeriodColumn,
    designation: showDesignationColumn,
    location: showLocationColumn,
    industry: showIndustryColumn,
    experience: showExperienceColumn,
    experienceYears: showExperienceYearsColumn,
    latestEducation: showLatestEducationColumn,
    skills: showSkillsColumn,
    age: showAgeColumn,
    gender: showGenderColumn,
    email: showEmailColumn,
    phone: showPhoneColumn,
    social: showSocialColumn,
    appliedDate: showAppliedDateColumn,
    completedAt: showCompletedAtColumn,
    shortlistedDate: showShortlistedDateEffective,
    rejectedDate: showRejectedDateEffective,
    salaryDetails: showSalaryDetailsColumn,
    // Not in the Columns dialog: forced on in the Go Colors Selected tab, off everywhere else.
    downloaded: isGoColors === true && showDownloadedColumn,
    proctoring: showProctoringColumn,
    documents: resolvedShowDocumentsColumn,
    duplicates: resolvedShowDuplicateColumn,
    // Every id in META_COLUMN_DEFAULT_ORDER must have an entry here. `orderedMetaColumns`
    // filters the order through this map, so a missing key is `undefined`, which is falsy,
    // which drops the column from the order — and the `case` that renders its header and its
    // cell then never runs. That is silent: nothing throws, the column simply does not exist.
    // `qualifyingCall` was missing here for its whole life, which is why a completed call
    // showed no transcript and no score however correct the data behind it was.
    qualifyingCall: resolvedShowQualifyingCallColumn,
    applicationForm: resolvedShowApplicationFormColumn,
    transcriptsAvailable: resolvedShowTranscriptsColumn,
    candidateDetails: showCandidateDetailsColumn,
  };
  const rawColumnOrder = Array.isArray(options?.columnOrder) ? options.columnOrder : [];
  const orderedMetaColumns = React.useMemo(() => {
    const visibleIds = META_COLUMN_DEFAULT_ORDER.filter((id) => metaColumnVisibilityMap[id]);
    // Priority: recruiter's saved order (localStorage) → per-tab org/platform default → META_COLUMN_DEFAULT_ORDER
    // NOTE: we intentionally do NOT fall back to user.ui.defaultCandidateColumnOrder here
    // because that candidates-specific order doesn't contain decision/notes, which would
    // cause them to be appended at the end of shortlist/rejected tab columns.
    const effectiveOrder = rawColumnOrder.length > 0 
      ? rawColumnOrder 
      : (options?.orgDefaultColumnOrder?.length > 0
          ? options.orgDefaultColumnOrder
          : []);
      
    let ordered = [];
    
    if (!effectiveOrder.length) {
      // Fall back to META_COLUMN_DEFAULT_ORDER which has decision/notes first
      ordered = [...visibleIds];
      
      // Inject visible custom fields after 'experience' so they are not hidden by default
      const customIds = registrationFields
        .filter((f) => !['name', 'email', 'phone', 'mobile'].includes(f.fieldId))
        .map((f) => `custom_${f.fieldId}`);
      
      if (customIds.length > 0) {
        // Only include those that are visible (or default to true)
        const visibleCustomIds = customIds.filter((id) => 
          options?.columnVisibility ? options.columnVisibility[id] !== false : true
        );
        if (visibleCustomIds.length > 0) {
          const experienceIdx = ordered.indexOf('experience');
          const insertIdx = experienceIdx !== -1 ? experienceIdx + 1 : ordered.length;
          ordered.splice(insertIdx, 0, ...visibleCustomIds);
        }
      }
    } else {
      const visibleSet = new Set(visibleIds);
      const validCustomIds = new Set(
        registrationFields.map((f) => `custom_${f.fieldId}`)
      );
      
      effectiveOrder.forEach((id) => {
        if (visibleSet.has(id)) {
          ordered.push(id);
          visibleSet.delete(id);
        } else if (id.startsWith('custom_')) {
          if (validCustomIds.has(id)) {
            const isVisible = options?.columnVisibility ? options.columnVisibility[id] !== false : true;
            if (isVisible) {
              ordered.push(id);
            }
          }
        }
      });

      // Inject missing store columns after storeRegion if applicable
      const storeRegionIdx = ordered.indexOf('storeRegion');
      if (storeRegionIdx !== -1) {
        const storeColsToInject = ['storeCity', 'storeState', 'storeCategory', 'armName', 'armEmail'].filter(c => !ordered.includes(c));
        storeColsToInject.forEach((col, i) => {
            ordered.splice(storeRegionIdx + 1 + i, 0, col);
            visibleSet.delete(col);
        });
      }

      const cityIdx = ordered.indexOf('cityCode');
      if (cityIdx === -1) {
        const emailIdx = ordered.indexOf('email');
        if (emailIdx !== -1) {
          ordered.splice(emailIdx, 0, 'cityCode', 'cityCity', 'cityState', 'cityRegion');
        }
      }

      // Inject NEW custom fields that are not yet in the saved order
      // (e.g. superadmin added DOB after the recruiter saved their column layout)
      const orderedSet = new Set(ordered);
      const newCustomIds = registrationFields
        .filter((f) => !['name', 'email', 'phone', 'mobile'].includes(f.fieldId))
        .map((f) => `custom_${f.fieldId}`)
        .filter((id) => !orderedSet.has(id));
      if (newCustomIds.length > 0) {
        const experienceIdx = ordered.indexOf('experience');
        const insertIdx = experienceIdx !== -1 ? experienceIdx + 1 : ordered.length;
        newCustomIds.forEach((id, i) => ordered.splice(insertIdx + i, 0, id));
      }

      // Whatever is still in visibleSet is a column that should be on screen and that this
      // saved order does not mention — almost always because it shipped after the order was
      // saved. Placed at its platform-default position rather than appended; see
      // insertColumnAtDefaultPosition for why the difference matters on a table this wide.
      // Iteration order is META_COLUMN_DEFAULT_ORDER order (visibleIds was built by filtering
      // it), so several new columns keep their intended order relative to each other.
      visibleSet.forEach((id) => insertColumnAtDefaultPosition(ordered, id));
    }

    if (visibleAssessmentModuleColumns && visibleAssessmentModuleColumns.length > 0) {
      const targetIdx = ordered.indexOf('assessmentScore');
      if (targetIdx !== -1) {
        ordered.splice(targetIdx, 0, 'assessmentModulesBlock');
      } else {
        const fallbackIdx = ordered.indexOf('overallScore');
        if (fallbackIdx !== -1) {
          ordered.splice(fallbackIdx, 0, 'assessmentModulesBlock');
        } else {
          ordered.unshift('assessmentModulesBlock');
        }
      }
    }
    return ordered;
  }, [
    rawColumnOrder.join(','),
    Object.values(metaColumnVisibilityMap).join(','),
    (options?.orgDefaultColumnOrder || []).join(','),
    options?.columnVisibility,
    registrationFields.map(f => f.fieldId).join(','),
  ]);



  if (rows.length === 0) {

    if (!showInlineEmptyState) {

      return (

        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white/60 px-6 py-10 text-center">

          <UsersIcon className="h-10 w-10 text-gray-400" />

          <p className="mt-3 text-sm font-medium text-gray-900">No candidates to show</p>

          <p className="mt-1 text-xs text-gray-500">

            Try adjusting your search or filters, or use the invite button to add candidates to

            this screening.

          </p>

        </div>

      );

    }



    // Empty-state colSpan must match the active header layout.

    const scoreColumnCount = isSimplifiedSingleMode

      ? 1

      : isDualMethodLayout

        ? 3

        : 3 + (showAssessment ? 1 : 0); // overall+resume+interview(+assessment)

    const baseColumnCount =

      1 + // Candidate

      scoreColumnCount +

      (showProctoringColumn ? 1 : 0) +

    visibleAssessmentModuleColumns.length +

    candidateMetaColumnCount + // meta columns (only in simplified)

      (showResumeInterviewExtraColumns ? 3 : 0) +

      (showResumeAssessmentExtraColumns ? 3 : 0) +

      (showCandidateDetailsColumn ? 1 : 0) +

      (resolvedShowDocumentsColumn ? 1 : 0) +

      (showCompletedAtColumn ? 1 : 0) +
      (!hideRowActions ? 1 : 0);



    const columnCount =

      baseColumnCount +

      (isSelectable ? 1 : 0) +

    (resolvedShowDuplicateColumn ? 1 : 0) +

      (showStatus ? 1 : 0) +

      (showShortlistedDateEffective ? 1 : 0) +

      (showRejectedDateEffective ? 1 : 0) +

      (isShortlistView ? 2 : 0);



    return (

      <div className="overflow-x-auto max-w-full scrollbar-thin-light pb-1">

        <table

          className="min-w-[1200px] w-full table-fixed border-collapse [&_th:first-child]:pl-6 [&_th:last-child]:pr-6 [&_td:first-child]:pl-6 [&_td:last-child]:pr-6"

          style={{ tableLayout: 'fixed' }}

        >

          <thead>

            <tr className="bg-[#007BFF]">

              {isSelectable && (

                <th className="px-2 py-3 text-left w-9 rounded-tl-xl table-cell" />

              )}

              <th

                className={`px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider ${!isSelectable ? 'rounded-tl-xl' : ''

                  } ${candidateNameWidthClass || (isResumeOnly ? 'w-[240px]' : '')}`}

              >

                Candidate

              </th>


              {showStatus && (

                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                  Status

                </th>

              )}

              {isShortlistView && (

                <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell w-[120px]">

                  Decision

                </th>

              )}



              {showOverallScoreColumn && (

                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">

                  Overall Score

                </th>

              )}



              {showResumeScoreColumn && (

                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                  Resume Score

                </th>

              )}



              {showAssessmentScoreColumn && (

                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-white table-cell">

                  <div className="flex items-center justify-center">

                    <span>Assessment Score</span>

                  </div>

                </th>

              )}


            <AssessmentModuleColumnHeaders modules={visibleAssessmentModuleColumns} />



            {showInterviewScoreColumn && (

                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-white table-cell">

                  {screeningMeta?.includeWhatsAppAgent ? 'WhatsApp Score' : 'Interview Score'}

                </th>

              )}



              {showShortlistedDateEffective && (

                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                  {isSamtaGroup ? 'Shortlisted Date' : 'Selected Date'}

                </th>

              )}



              {showRejectedDateEffective && (

                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                  Rejected Date

                </th>

              )}



              {showProctoringColumn && (

                <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">

                  Proctoring

                </th>

              )}

              {resolvedShowDocumentsColumn && (
                <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">
                  DOCUMENTS
                </th>
              )}

              {isShortlistView && (

                <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">

                  Notes

                </th>

              )}





              {showCandidateDetailsColumn && (

                <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">

                  Candidate details

                </th>

              )}



              {showResumeAssessmentExtraColumns && (

                <>

                  <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">

                    Current Company

                  </th>

                  <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">

                    Age

                  </th>

                  <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">

                    Experience

                  </th>

                </>

              )}



              {showResumeInterviewExtraColumns && (

                <>

                  <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">

                    Current Company

                  </th>

                  <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">

                    Age

                  </th>

                  <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">

                    Experience

                  </th>

                </>

              )}



              {showCompletedAtColumn && (

                <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">

                  Completed at

                </th>

              )}

              {!hideRowActions && (
                <th className="px-4 py-3 text-right text-xs font-semibold text-white uppercase tracking-wider rounded-tr-xl">

                  Actions

                </th>

              )}

            </tr>

          </thead>

          <tbody>

            <tr>

              <td colSpan={columnCount} className="px-6 py-8 text-center text-sm text-gray-600 bg-white border-b border-l border-r border-gray-300/50">

                {isAISearchActiveForTable

                  ? 'No candidates in this screening match your AI search or filters.'

                  : 'No candidates in this screening match your current filters.'}

                {typeof onClearFilters === 'function' && (

                  <button

                    type="button"

                    onClick={onClearFilters}

                    className="ml-2 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800"

                  >

                    Clear filters

                  </button>

                )}

              </td>

            </tr>

          </tbody>

        </table>

      </div>

    );

  }

  const formatShortlistedDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
      : '—';

  const formatRejectedDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
      : '—';

  const formatAppliedDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatSimpleList = (list) => {
    if (!Array.isArray(list) || list.length === 0) return '—';
    return list.filter(Boolean).join(', ') || '—';
  };

  const summarizeSocialProfiles = (candidate) => {
    if (!candidate) return '—';
    const labels = new Set();
    const pushLabel = (label, url) => {
      if (!url) return;
      labels.add(label);
    };
    pushLabel('LinkedIn', candidate.linkedinUrl);
    pushLabel('GitHub', candidate.githubUrl);
    pushLabel('Behance', candidate.behanceUrl);
    pushLabel('Website', candidate.websiteUrl);
    pushLabel('Portfolio', candidate.portfolioUrl);
    if (Array.isArray(candidate.profileLinks)) {
      candidate.profileLinks.forEach((link) => {
        const label = String(link?.label || link?.domain || 'Link').trim();
        const url = link?.url;
        if (label && url) {
          labels.add(label);
        }
      });
    }
    return labels.size ? Array.from(labels).slice(0, 4).join(', ') : '—';
  };

  const collectSocialLinks = (candidate) => {
    if (!candidate) return [];
    const links = [];
    const seen = new Set();
    const add = (label, url) => {
      if (!url) return;
      const key = url.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      links.push({ label, url });
    };
    add('LinkedIn', candidate.linkedinUrl);
    add('GitHub', candidate.githubUrl);
    add('Behance', candidate.behanceUrl);
    add('Website', candidate.websiteUrl);
    add('Portfolio', candidate.portfolioUrl);
    if (Array.isArray(candidate.profileLinks)) {
      candidate.profileLinks.forEach((link) => {
        const label = String(link?.label || link?.domain || 'Link').trim();
        add(label, link?.url);
      });
    }
    return links;
  };

  const SOCIAL_ICON_MAP = {
    linkedin: { Icon: Linkedin, color: '#0A66C2' },
    github: { Icon: Github, color: '#333' },
    behance: { Icon: PenTool, color: '#1769FF' },
    portfolio: { Icon: Briefcase, color: '#6B7280' },
    website: { Icon: Globe, color: '#6B7280' },
  };

  const getSocialIcon = (label) => {
    const key = (label || '').toLowerCase();
    for (const [match, val] of Object.entries(SOCIAL_ICON_MAP)) {
      if (key.includes(match)) return val;
    }
    return { Icon: LinkIcon, color: '#6B7280' };
  };

  const SocialLinksCell = ({ candidate }) => {
    const links = collectSocialLinks(candidate);
    if (!links.length) return <span className="text-gray-400">—</span>;
    return (
      <div className="flex items-center gap-1.5">
        {links.slice(0, 4).map((link) => {
          const { Icon, color } = getSocialIcon(link.label);
          return (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              title={link.label}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center justify-center rounded p-0.5 hover:bg-gray-100 transition-colors"
            >
              <Icon size={15} style={{ color }} strokeWidth={1.8} />
            </a>
          );
        })}
      </div>
    );
  };

  const formatDateTime = (d) =>
    d
      ? new Date(d).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
      : '';

  // Normalizes display text to "Title Case (rest lowercase)".

  // Example: "mahesh prajapati" => "Mahesh Prajapati"

  // Keeps common separators like spaces, hyphens, and apostrophes.

  const normalizeTitleCaseLowerRemainder = (value) => {

    const raw = value == null ? '' : String(value).trim();

    if (!raw) return '';



    const normalizeWordCore = (word) => {

      const leading = word.match(/^[^A-Za-z]*/)?.[0] ?? '';

      const trailing = word.match(/[^A-Za-z]*$/)?.[0] ?? '';

      const core = word.slice(leading.length, word.length - trailing.length);



      if (!core) return word;

      if (!/^[A-Za-z]/.test(core)) return word; // avoid changing tokens that start with digits/symbols



      const parts = core.split(/([-'])/); // keep '-' and "'" in the result

      const normalizedParts = parts.map((p) => {

        if (p === '-' || p === "'") return p;

        const lower = p.toLowerCase();

        if (!lower) return p;

        return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;

      });



      return `${leading}${normalizedParts.join('')}${trailing}`;

    };



    return raw

      .split(/\s+/)

      .map(normalizeWordCore)

      .join(' ');

  };



  // Rounds experience values to whole years for display.

  // Examples: 2.9 => 3, 2.2 => 2

  const formatExperienceYearsDisplay = (value) => {

    if (value == null) return '—';

    if (typeof value === 'number') {

      if (Number.isNaN(value)) return '—';

      return String(Math.round(value));

    }

    const raw = String(value).trim();

    if (!raw) return '—';



    const rangeMatch = raw.match(/(\d+(?:\.\d+)?)\s*[-–to]+\s*(\d+(?:\.\d+)?)/i);

    const singleMatch = rangeMatch

      ? null

      : raw.match(/(\d+(?:\.\d+)?)/);



    const n = rangeMatch

      ? (parseFloat(rangeMatch[1], 10) + parseFloat(rangeMatch[2], 10)) / 2

      : singleMatch

        ? parseFloat(singleMatch[1], 10)

        : NaN;



    if (Number.isNaN(n)) return raw;

    return String(Math.round(n));

  };



  const formatExperienceYearsDisplayWithUnit = (value) => {

    const v = formatExperienceYearsDisplay(value);

    if (v === '—') return '—';

    return `${v} years`;

  };



  // Abbreviate "latest education" strings for compact UI.

  // Examples:

  // - "bachelors in technology" => "BTech"

  // - "mba in psychology" => "MBA"

  // - "masters in physic" => "M. Phy"

  // - "phd", "diploma", "10th", "12th" => normalized compact labels

  const formatLatestEducationAbbrev = (education) => {

    const raw = education == null ? '' : String(education).trim();

    if (!raw) return '—';

    const s = raw.toLowerCase();



    if (/\b10\s*(th)?\b/.test(s) || /\bclass\s*10\b/.test(s)) return '10th';

    if (/\b12\s*(th)?\b/.test(s) || /\bclass\s*12\b/.test(s)) return '12th';



    // Doctorates / post-grads

    if (/\b(phd|ph\.?\s*d|doctorate|d\.?\s*phil)\b/.test(s)) return 'PhD';

    if (/\bmba\b/.test(s)) return 'MBA';



    if (/\bpg\s*diploma\b/.test(s) || /\bpost\s*graduate\s*diploma\b/.test(s)) return 'PG Diploma';

    if (/\b(post\s*graduate\s*diploma|postgraduate\s*diploma)\b/.test(s)) return 'PG Diploma';



    if (/\bdiploma\b/.test(s)) return 'Diploma';



    // MCA variants

    if (/\bmca\b/.test(s) || /\bmaster\s+of\s+computer\s+application\b/.test(s) || /\bcomputer\s+application\b/.test(s)) {

      return 'MCA';

    }



    // B.E. variants

    if (

      /\bb\.?\s*e\.?\b/.test(s) ||

      /\bbachelor\s+of\s+engineering\b/.test(s) ||

      /\bbachelor\s+of\s+engineering\b/.test(s)

    ) {

      return 'B.E';

    }



    // B.Sc variants (per your requirement: show as B.Sc.IT for computer science)

    if (

      /\bb\.?\s*sc\.?\b/.test(s) ||

      /\bbsc\b/.test(s) ||

      /\bbachelor\s+of\s+science\b/.test(s)

    ) {

      return 'B.Sc.IT';

    }



    // M.Sc variants (per your requirement: show M.Sc.IT for Information Technology / Computer Science)

    if (/\bmaster\s+of\s+science\b/.test(s) || /\bmsc\b/.test(s) || /\bm\.?\s*sc\b/.test(s) || /\bmasters?\b/.test(s)) {

      const isITorCS = /\binformation\s+technology\b/.test(s) || /\bit\b/.test(s) || /\bcomputer\s+science\b/.test(s) || /\bcomputer\s+science\b/.test(s);

      if (isITorCS) return 'M.Sc.IT';

      return 'M.Sc';

    }



    // BTech variants

    const isTechBachelor =

      /\b(bachelor(?:s)?|b\.?\s*tech|btech)\b/.test(s) && /\b(tech|technology)\b/.test(s);

    if (isTechBachelor) return 'B.Tech';



    // Masters fallback (for non-M.Sc)

    if (/\bmaster|m\.?\s*tech|mtech|msc|m\.?\s*s|masters\b/.test(s)) {

      if (/\bphysic|physics\b/.test(s)) return 'M. Phy';

      if (/\b(tech|technology)\b/.test(s)) return 'MTech';

      return 'Masters';

    }



    // Bachelors fallback

    if (/\bbachelor/.test(s)) return 'Bachelors';



    // Fallback: compact but still readable.

    return normalizeTitleCaseLowerRemainder(raw.length > 30 ? raw.slice(0, 30).trim() : raw);

  };



  const renderCompanyName = (company) => {

    const companyName = company ? String(company).trim() : '';

    if (!companyName) return '—';

    const normalized = normalizeTitleCaseLowerRemainder(companyName);

    const maxWidth = isResumeOnly

      ? 240

      : (isResumeAssessmentDual || isResumeInterviewDual)

        ? 200

        : 170;

    return (

      <span

        className="inline-block truncate align-middle"

        style={{ maxWidth }}

        title={normalized}

      >

        {normalized}

      </span>

    );

  };



  const toggleSelection = (id) => {

    if (!onSelectionChange) return;

    onSelectionChange((prev) =>

      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]

    );

  };

  const toggleSelectAll = () => {

    if (!onSelectionChange) return;

    const ids = rows.map((c) => c.id || c._id);

    const allSelected = ids.every((id) => selectedIds.includes(id));

    onSelectionChange(allSelected ? [] : ids);

  };



  const getAssessmentPctForRow = (c) => {

  if (!c || c.assessmentScore == null) return null;

  const total = c.assessmentTotalPoints || screeningMeta?.assessmentTotalPoints || screeningMeta?.assessment?.totalPoints;

  if (!total) return null;

    if (total > 0) return Math.round((c.assessmentScore / total) * 100);

    return Math.round(c.assessmentScore);

  };



  return (
    <>

      <div className="overflow-x-auto max-w-full scrollbar-thin-light pb-1">

        <table

          data-testid="candidates-table"

          className="w-full border-collapse [&_th:first-child]:pl-5 [&_th:last-child]:pr-8 [&_td:first-child]:pl-5 [&_td:last-child]:pr-8"

          style={{ tableLayout: 'auto' }}

        >

          <thead className="sticky top-0 z-10">

            <tr className="bg-[#007BFF]">

              {isSelectable && (

                <th className="px-2 py-3 text-left w-9 rounded-tl-xl table-cell sticky left-0 z-20 bg-[#007BFF]" style={{ boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)' }}>

                  <input

                    type="checkbox"

                    checked={

                      rows.length > 0 && rows.every((c) => selectedIds.includes(c.id || c._id))

                    }

                    onChange={toggleSelectAll}

                    className="h-3.5 w-3.5 border-2 border-white rounded cursor-pointer accent-white bg-transparent focus:ring-white/50 focus:ring-offset-0 focus:ring-2"

                  />

                </th>

              )}

              <th

                className={`px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider ${!isSelectable ? 'rounded-tl-xl' : ''

                  } ${candidateNameWidthClass || (isResumeOnly ? 'w-[240px]' : '')}`}

              >

                <div className="flex items-center gap-1">

                  <span>Candidate</span>

                </div>

              </th>





























              {showResumeAssessmentExtraColumns && (

                <>

                  <th className={`px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell ${companyWidthClass}`}>

                    Current Company

                  </th>

                  {showResumeAssessmentExperienceColumn && (

                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                      <div className="flex items-center justify-center">

                        <span>Experience</span>

                        {!hideColumnFilters && (
                          <MetaFilterDropdown

                            id="experience"

                            label="Experience"

                            value={metaFilters.experience}

                            options={expOptions}

                            isOpen={openMetaFilterId === 'experience'}

                            onToggleOpen={(id) => setOpenMetaFilterId((prev) => (prev === id ? null : id))}

                            onRequestClose={() => setOpenMetaFilterId(null)}

                            onChange={(v) => setMetaFilters((prev) => ({ ...prev, experience: v }))}

                            inverted

                          />
                        )}

                      </div>

                    </th>

                  )}

                </>

              )}



              {showResumeInterviewExtraColumns && (

                <>

                  <th className={`px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell ${companyWidthClass}`}>

                    Current Company

                  </th>

                  {showResumeInterviewExperienceColumn && (

                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                      <div className="flex items-center justify-center">

                        <span>Experience</span>

                        {!hideColumnFilters && (
                          <MetaFilterDropdown

                            id="experience"

                            label="Experience"

                            value={metaFilters.experience}

                            options={expOptions}

                            isOpen={openMetaFilterId === 'experience'}

                            onToggleOpen={(id) => setOpenMetaFilterId((prev) => (prev === id ? null : id))}

                            onRequestClose={() => setOpenMetaFilterId(null)}

                            onChange={(v) => setMetaFilters((prev) => ({ ...prev, experience: v }))}

                            inverted

                          />
                        )}

                      </div>

                    </th>

                  )}

                </>

              )}



              {/* ── Orderable meta columns (headers) ── */}
              {orderedMetaColumns.map((colId) => {
                if (colId.startsWith('custom_')) {
                  const fieldIdMatch = colId.replace('custom_', '');
                  // Check if there is a field matching the ID in user's organization fields
                  const customFieldDef = registrationFields.find(
                    (f) => f.fieldId === fieldIdMatch || `custom_${f.fieldId}` === colId || f.fieldId === colId
                  );
                  const label = customFieldDef?.label || fieldIdMatch;
                  return (
                    <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">
                      {label}
                    </th>
                  );
                }
                switch (colId) {
                                    case 'status':
                    return               showStatus ? (

                <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                  <div className="flex items-center justify-center">

                    <span>Status</span>

                    {!hideColumnFilters && (
                      <ScoreFilterDropdown

                        id="status"

                        label="Status"

                        value={scoreFilters.status}

                        isOpen={openScoreFilterId === 'status'}

                        onToggleOpen={(id) =>

                          setOpenScoreFilterId((prev) => (prev === id ? null : id))

                        }

                        onRequestClose={() => setOpenScoreFilterId(null)}

                        onChange={(v) =>

                          setScoreFilters((prev) => ({

                            ...prev,

                            status: v,

                          }))

                        }

                        inverted

                      />
                    )}

                  </div>

                </th>

              ) : null;

              case 'decision':
                return showDecisionColumn ? (
                  <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell w-[120px]">
                    Decision
                  </th>
                ) : null;

              case 'notes':
                return showNotesColumn ? (
                  <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">
                    Notes
                  </th>
                ) : null;

                                    case 'resumeScore':
                    return               showResumeScoreColumn ? (

                <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                  <div className="flex items-center justify-center">

                    <span>Resume Score</span>

                    {!hideColumnFilters && (
                      <ScoreFilterDropdown

                        id="resume"

                        label="Resume Score"

                        value={scoreFilters.resumeMin}

                        isOpen={openScoreFilterId === 'resume'}

                        onToggleOpen={(id) => setOpenScoreFilterId((prev) => (prev === id ? null : id))}

                        onRequestClose={() => setOpenScoreFilterId(null)}

                        onChange={(v) => setScoreFilters((prev) => ({ ...prev, resumeMin: v }))}

                        inverted

                      />
                    )}

                  </div>

                </th>

              ) : null;

                                    case 'duplicates':
                    return           resolvedShowDuplicateColumn ? (
                <th key={colId} className="px-3 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider w-[120px]">
                  Duplicates
                </th>
              ) : null;

                  
                  case 'assessmentModulesBlock':
                    return <AssessmentModuleColumnHeaders key="assessmentModulesBlock" modules={visibleAssessmentModuleColumns} />;
                  case 'overallScore':
                    return               showOverallScoreColumn ? (

                <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">

                  <div className="flex items-center justify-center">

                    <span>Overall Score</span>

                    {!hideColumnFilters && (
                      <ScoreFilterDropdown

                        id="overall"

                        label="Overall Score"

                        value={scoreFilters.overallMin}

                        isOpen={openScoreFilterId === 'overall'}

                        onToggleOpen={(id) => setOpenScoreFilterId((prev) => (prev === id ? null : id))}

                        onRequestClose={() => setOpenScoreFilterId(null)}

                        onChange={(v) => setScoreFilters((prev) => ({ ...prev, overallMin: v }))}

                        inverted

                      />
                    )}

                  </div>

                </th>

              ) : null;

                  case 'assessmentScore':
                    return               showAssessmentScoreColumn ? (

                <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                  <div className="flex items-center justify-center">

                    <span>Assessment Score</span>

                    {!hideColumnFilters && (
                      <ScoreFilterDropdown

                        id="assessment"

                        label="Assessment Score"

                        value={scoreFilters.assessmentMin}

                        isOpen={openScoreFilterId === 'assessment'}

                        onToggleOpen={(id) => setOpenScoreFilterId((prev) => (prev === id ? null : id))}

                        onRequestClose={() => setOpenScoreFilterId(null)}

                        onChange={(v) => setScoreFilters((prev) => ({ ...prev, assessmentMin: v }))}

                        inverted

                      />
                    )}

                  </div>

                </th>

              ) : null;

                  case 'interviewScore':
                    return               showInterviewScoreColumn ? (

                <th key={colId} className="px-3 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                  <div className="flex items-center justify-center">

                    <span>{screeningMeta?.includeWhatsAppAgent ? 'WhatsApp Score' : 'Interview Score'}</span>

                    {!hideColumnFilters && (
                      <ScoreFilterDropdown

                        id="interview"

                        label={screeningMeta?.includeWhatsAppAgent ? 'WhatsApp Score' : 'Interview Score'}

                        value={scoreFilters.interviewMin}

                        isOpen={openScoreFilterId === 'interview'}

                        onToggleOpen={(id) => setOpenScoreFilterId((prev) => (prev === id ? null : id))}

                        onRequestClose={() => setOpenScoreFilterId(null)}

                        onChange={(v) => setScoreFilters((prev) => ({ ...prev, interviewMin: v }))}

                        inverted

                      />
                    )}

                  </div>

                </th>

              ) : null;

                  case 'shortlistedDate':
                    return               showShortlistedDateEffective ? (

                <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                  {isSamtaGroup ? 'Shortlisted Date' : 'Selected Date'}

                </th>

              ) : null;

                  case 'rejectedDate':
                    return               showRejectedDateEffective ? (

                <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                  Rejected Date

                </th>

              ) : null;

                  case 'proctoring':
                    return               showProctoringColumn ? (

                <th key={colId} className="px-3 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">

                  <div className="flex items-center justify-center">

                    <span>Proctoring</span>

                    {!hideColumnFilters && (
                      <MetaFilterDropdown

                        id="proctoring"

                        label="Proctoring"

                        value={metaFilters.proctoring}

                        options={proctoringOptions}

                        isOpen={openMetaFilterId === 'proctoring'}

                        onToggleOpen={(id) => setOpenMetaFilterId((prev) => (prev === id ? null : id))}

                        onRequestClose={() => setOpenMetaFilterId(null)}

                        onChange={(v) => setMetaFilters((prev) => ({ ...prev, proctoring: v }))}

                        inverted

                      />
                    )}

                  </div>

                </th>

              ) : null;

                  case 'companyName':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[140px] min-w-[140px] max-w-[140px]">
                        Current Company
                      </th>
                    );
                  case 'storeId':
                    return (
                      <th key={colId} className="px-3 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell w-[110px] min-w-[110px] max-w-[110px]">
                        Store Code
                      </th>
                    );
                  case 'storeName':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[140px] min-w-[140px] max-w-[140px]">
                        Store Name
                      </th>
                    );
                  case 'storeRegion':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[120px] min-w-[120px] max-w-[120px]">
                        Store Region
                      </th>
                    );
                  case 'storeCity':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[120px] min-w-[120px] max-w-[120px]">
                        Store City
                      </th>
                    );
                  case 'storeState':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[120px] min-w-[120px] max-w-[120px]">
                        Store State
                      </th>
                    );
                  case 'storeCategory':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[120px] min-w-[120px] max-w-[120px]">
                        Store Category
                      </th>
                    );
                  case 'armName':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[140px] min-w-[140px] max-w-[140px]">
                        ARM Name
                      </th>
                    );
                  case 'armEmail':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[160px] min-w-[160px] max-w-[160px]">
                        ARM Email
                      </th>
                    );
                  case 'cityCode':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[120px] min-w-[120px] max-w-[120px]">
                        City Code
                      </th>
                    );
                  case 'cityCity':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[120px] min-w-[120px] max-w-[120px]">
                        City Name
                      </th>
                    );
                  case 'cityState':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[120px] min-w-[120px] max-w-[120px]">
                        City State
                      </th>
                    );
                  case 'cityRegion':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[120px] min-w-[120px] max-w-[120px]">
                        City Region
                      </th>
                    );
              case 'currentCtc':
                return (
                  <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[140px] min-w-[140px] max-w-[140px]">
                    Current CTC
                  </th>
                );
              case 'noticePeriod':
                return (
                  <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[140px] min-w-[140px] max-w-[140px]">
                    Notice Period
                  </th>
                );
                  case 'screening':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider table-cell w-[170px] min-w-[170px] max-w-[170px]">
                        Screening
                      </th>
                    );
                  case 'designation':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-medium text-white uppercase tracking-wider table-cell w-[150px] min-w-[150px] max-w-[150px]">
                        Designation
                      </th>
                    );
                  case 'location':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-medium text-white uppercase tracking-wider table-cell w-[110px] min-w-[110px] max-w-[110px]">
                        Location
                      </th>
                    );
                  case 'industry':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-medium text-white uppercase tracking-wider table-cell w-[120px] min-w-[120px] max-w-[120px]">
                        Industry
                      </th>
                    );
                  case 'experience':
                    return (
                      <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">
                        <div className="flex items-center justify-center">
                          <span>Experience</span>
                          {!hideColumnFilters && (
                            <MetaFilterDropdown
                              id="experience"
                              label="Experience"
                              value={metaFilters.experience}
                              options={expOptions}
                              isOpen={openMetaFilterId === 'experience'}
                              onToggleOpen={(id) => setOpenMetaFilterId((prev) => (prev === id ? null : id))}
                              onRequestClose={() => setOpenMetaFilterId(null)}
                              onChange={(v) => setMetaFilters((prev) => ({ ...prev, experience: v }))}
                              inverted
                            />
                          )}
                        </div>
                      </th>
                    );
              case 'experienceYears':
                return (
                  <th key={colId} className="px-3 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell w-[110px] min-w-[110px] max-w-[110px]">
                    Exp (Years)
                  </th>
                );
              case 'age':
                return (
                  <th key={colId} className="px-3 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell w-[80px] min-w-[80px] max-w-[80px]">
                    Age
                  </th>
                );
              case 'gender':
                return (
                  <th key={colId} className="px-3 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell w-[80px] min-w-[80px] max-w-[80px]">
                    Gender
                  </th>
                );
                  case 'latestEducation':
                    return (
                      <th key={colId} className="px-3 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell w-[100px] min-w-[100px] max-w-[100px]">
                        Education
                      </th>
                    );
                  case 'skills':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-medium text-white uppercase tracking-wider table-cell w-[150px] min-w-[150px] max-w-[150px]">
                        Skills
                      </th>
                    );
                  case 'email':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-medium text-white uppercase tracking-wider table-cell w-[180px] min-w-[180px] max-w-[180px]">
                        Email
                      </th>
                    );
                  case 'phone':
                    return (
                      <th key={colId} className="px-3 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell w-[150px] min-w-[150px] max-w-[150px]">
                        Phone
                      </th>
                    );
                  case 'social':
                    return (
                      <th key={colId} className="px-3 py-3 text-left text-xs font-medium text-white uppercase tracking-wider table-cell w-[130px] min-w-[130px] max-w-[130px]">
                        Social
                      </th>
                    );
                  case 'appliedDate':
                    return (
                      <th key={colId} className="px-3 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell w-[95px] min-w-[95px] max-w-[95px]">
                        Applied
                      </th>
                    );
                  case 'completedAt':
                    return (
                      <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">
                        Completed at
                      </th>
                    );
                  case 'downloaded':
                    return (
                      <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">
                        Downloaded
                      </th>
                    );
                  case 'salaryDetails':
                    return showSalaryDetailsColumn ? (
                      <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">
                        Salary Details
                      </th>
                    ) : null;
                  case 'documents':
                    return resolvedShowDocumentsColumn ? (
                      <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">
                        Documents
                      </th>
                    ) : null;
                  case 'qualifyingCall':
                    return resolvedShowQualifyingCallColumn ? (
                      <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">
                        Qualifying Call
                      </th>
                    ) : null;
                  case 'applicationForm':
                    return resolvedShowApplicationFormColumn ? (
                      <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">
                        Application Form
                      </th>
                    ) : null;
                  case 'transcriptsAvailable':
                    return resolvedShowTranscriptsColumn ? (
                      <th key={colId} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider table-cell">
                        Transcripts Available
                      </th>
                    ) : null;
                  default:
                    return null;
                }
              })}



              {showCandidateDetailsColumn && (
                <th className="px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider table-cell">
                  Candidate details
                </th>
              )}


              {!hideRowActions && (
                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider sticky right-0 z-20 bg-[#007BFF]" style={{ boxShadow: '-3px 0 6px -3px rgba(0,0,0,0.15)' }}>

                  Actions

                </th>
              )}

            </tr>

          </thead>

          <tbody>

            {(onLoadMoreProp ? rows : rows.slice(0, displayCount)).map((c) => {

              const candidateId = c.id || c._id;

              const candidateFullName = resolveCandidateDisplayName(c) || 'Unknown';

              const isResumeDropped = c.resumeScore === 0 && !c.hasResumeScoreDetail;
              const isAssessmentDropped = c.assessmentScore === 0 && !c.hasAssessmentScoreDetail;
              const isInterviewDropped = c.interviewScore === 0 && !c.hasInterviewScoreDetail;
              const hasAnyDroppedStage = isResumeDropped || isAssessmentDropped || isInterviewDropped;
              // "In Progress" is not the same as "no results yet" — a candidate can finish the
              // resume, assessment and interview and still sit at In Progress because one
              // required document is outstanding. Blanking every score column on status alone
              // hides results that are already in the database, so gate on the value instead.
              const rowAssessmentPct = getAssessmentPctForRow(c);
              const hasAnyStageScore =
                c.resumeScore != null || rowAssessmentPct != null || c.interviewScore != null;

              return (

                <tr

                  key={candidateId}

                  className={`group transition-colors border-b border-l border-r border-gray-300/50 ${
                    c.isAllZero
                      ? 'bg-gray-50/70 opacity-60'
                      : 'bg-white hover:bg-[#8472B3]/10'
                  }`}

                >

                  {isSelectable && (

                    <td className="px-2 py-3 w-9 table-cell sticky left-0 z-10" style={{ boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>

                      <input

                        type="checkbox"
                        data-testid="candidate-checkbox"
                        checked={selectedIds.includes(candidateId)}

                        onChange={() => toggleSelection(candidateId)}

                        className="h-3.5 w-3.5 text-[#007BFF] focus:ring-[#007BFF]/50 border-gray-300 rounded cursor-pointer"

                        onClick={(e) => e.stopPropagation()}

                      />

                    </td>

                  )}

                  <td className={`px-4 py-3 min-w-0 ${candidateNameWidthClass || (isResumeOnly ? 'w-[240px]' : '')}`}>

                    <div className="min-w-0">

                      <div className="flex items-center gap-1.5 min-w-0">

                        <span
                          className={`text-sm truncate text-[#242124] ${
                            !c.isViewed ? 'font-semibold' : 'font-medium'
                          }`}
                          title={candidateFullName}
                        >
                          {candidateFullName}
                        </span>

                    {!c.isViewed && (
                          <span
                            className="inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-500 ring-2 ring-white"
                            title="Not reviewed yet"
                            aria-label="Not reviewed yet"
                          />
                        )}

                        {showRecommendedChip && (() => {
                          if (c.status !== 'Completed' || c.isInterviewIncomplete || c.isAllZero) return null;
                          let score = null;
                          if (isSimplifiedSingleMode) {
                            if (isResumeOnly) score = c.resumeScore;
                            else if (isAssessmentOnly) score = getAssessmentPctForRow(c);
                            else if (isInterviewOnly) score = c.interviewScore;
                          } else {
                            const parts = [];
                            if (c.resumeScore != null) parts.push(c.resumeScore);
                            const ap = getAssessmentPctForRow(c);
                            if (ap != null) parts.push(ap);
                            if (c.interviewScore != null) parts.push(c.interviewScore);
                            if (parts.length > 0) score = Math.round(parts.reduce((s, v) => s + v, 0) / parts.length);
                          }
                          if (score == null || score < recommendedChipThreshold) return null;
                          return (
                            <span title="Recommended" className="inline-flex items-center justify-center shrink-0 rounded-full bg-amber-50 p-0.5 ring-1 ring-amber-200">
                              <StarIconSolid className="h-3.5 w-3.5 text-amber-400" />
                            </span>
                          );
                        })()}

                      </div>

                      <div className="md:hidden text-xs text-[#242124] mt-0.5">

                        {showStatus && (

                          <span

                            title={

                              c.rejected
                                ? c.rejectedAt
                                  ? `Rejected on ${formatDateTime(c.rejectedAt)}`
                                  : undefined
                                : c.shortlisted

                                ? c.shortlistedAt

                                  ? `${isSamtaGroup ? 'Shortlisted' : 'Selected'} on ${formatDateTime(c.shortlistedAt)}`

                                  : undefined

                                : c.status === 'Completed'

                                  ? c.completedAt

                                    ? `Completed on ${formatDateTime(c.completedAt)}`

                                    : undefined

                                  : c.isProcessingScores

                                    ? 'Interview completed, calculating scores...'

                                    : c.status === 'In Progress'

                                      ? c.screeningStartedAt

                                        ? `Started on ${formatDateTime(c.screeningStartedAt)}`

                                        : undefined

                                      : c.invitedAt

                                        ? `Invited on ${formatDateTime(c.invitedAt)}`

                                        : undefined

                            }

                          >
                            {c.rejected ? 'Rejected' : c.pendingReuploadDocuments?.length > 0 ? 'Pending Documents' : c.shortlisted ? (isSamtaGroup ? 'Shortlisted' : 'Selected') : c.isProcessingScores ? 'In Progress' : (c.status ? (c.status.charAt(0).toUpperCase() + c.status.slice(1)) : 'Invited')}
                          </span>
                        )}
                        {((c.status === 'In Progress') || c.isProcessingScores) ? (

                          <span className="inline-flex items-center gap-1">

                            {showStatus ? ' · ' : ''}

                            <span className={`inline-block h-3 w-3 animate-spin rounded-full border-2 ${c.isProcessingScores ? 'border-amber-300 border-t-amber-600' : 'border-gray-300 border-t-indigo-600'}`} aria-hidden />

                          </span>

                        ) : (() => {

                          if (isSimplifiedSingleMode) {

                            const label = isResumeOnly ? 'Resume' : isAssessmentOnly ? 'Assessment' : 'Interview';

                            let scoreForMode = null;



                            if (isResumeOnly) {

                              scoreForMode = c.resumeScore;

                            } else if (isAssessmentOnly) {

                              if (c.assessmentScore != null) {

                                const totalPoints = c.assessmentTotalPoints;

                                scoreForMode =

                                  totalPoints && totalPoints > 0

                                    ? Math.round((c.assessmentScore / totalPoints) * 100)

                                    : Math.round(c.assessmentScore);

                              }

                            } else if (isInterviewOnly) {

                              scoreForMode = c.interviewScore;

                            }



                            if (scoreForMode == null) return null;



                            return (

                              <span>

                                {showStatus ? ' · ' : ''}

                                {label} {scoreForMode}%

                              </span>

                            );

                          }



                          const scoresObj = {};
                          if (!isAssessmentOnly && !isInterviewOnly && !isAssessmentInterviewDual) {
                            scoresObj.resume = c.resumeScore;
                          }
                          if (!isResumeOnly && !isInterviewOnly && !isResumeInterviewDual) {
                            scoresObj.assessment = getAssessmentPctForRow(c);
                          }
                          if (!isResumeOnly && !isAssessmentOnly && !isResumeAssessmentDual) {
                            scoresObj.interview = c.interviewScore;
                          }

                          const overallScore = calculateWeightedOverallScore(scoresObj, weightsConfig);
                          if (overallScore == null) return null;

                          return (
                            <span>
                              {showStatus ? ' · ' : ''}
                              Overall {overallScore}%
                            </span>
                          );
                        })()}
                      </div>

                    </div>

                  </td>














                  {showResumeAssessmentExtraColumns && (
                    <>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm text-center table-cell ${companyWidthClass}`}>
                        {renderCompanyName(c.company)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell">
                        {formatExperienceYearsDisplayWithUnit(
                          c.experienceYears != null ? c.experienceYears : c.experience
                        )}
                      </td>
                      {showResumeAssessmentExperienceColumn && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell">
                          {formatExperienceYearsDisplayWithUnit(
                            c.experienceYears != null ? c.experienceYears : c.experience
                          )}
                        </td>
                      )}
                    </>
                  )}



                  {showResumeInterviewExtraColumns && (
                    <>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm text-center table-cell ${companyWidthClass}`}>
                        {renderCompanyName(c.company)}
                      </td>
                      {showResumeInterviewExperienceColumn && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell">
                          {formatExperienceYearsDisplayWithUnit(
                            c.experienceYears != null ? c.experienceYears : c.experience
                          )}
                        </td>
                      )}
                    </>
                  )}



                  {/* Candidate feedback column temporarily replaced with completion time */}

                  {/*

              <td

                className="px-4 py-3 whitespace-nowrap text-center table-cell"

                onClick={(e) => e.stopPropagation()}

              >

                {c.status === 'In Progress' ? (

                  <span className="text-gray-400">—</span>

                ) : c.feedbackStars != null && c.feedbackStars >= 1 ? (

                  <span

                    className="inline-flex items-center gap-0.5"

                    title={[

                      c.feedback ? `Comment: ${c.feedback}` : null,

                      c.feedbackAt ? `Date: ${formatDateTime(c.feedbackAt)}` : null,

                    ]

                      .filter(Boolean)

                      .join('\n')}

                  >

                    {[1, 2, 3, 4, 5].map((n) =>

                      n <= (c.feedbackStars || 0) ? (

                        <StarIconSolid key={n} className="h-4 w-4 text-amber-400" aria-hidden />

                      ) : (

                        <StarIcon key={n} className="h-4 w-4 text-gray-300" aria-hidden />

                      )

                    )}

                  </span>

                ) : (

                  <span className="text-gray-400">—</span>

                )}

              </td>

              */}



                  {/* ── Orderable meta columns (body cells) ── */}
                  {orderedMetaColumns.map((colId) => {
                    if (colId.startsWith('custom_')) {
                      const fieldIdMatch = colId.replace('custom_', '');
                      
                      const customFieldDef = registrationFields.find(
                        (f) => f.fieldId === fieldIdMatch || `custom_${f.fieldId}` === colId || f.fieldId === colId
                      );
                      const label = customFieldDef?.label || fieldIdMatch;

                      // Look up the value in candidate.customFields by fieldId, custom_fieldId
                      let customValue = c.customFields?.[fieldIdMatch] || c.customFields?.[colId] || c.customFields?.[label];
                      
                      // Robust fallback for legacy candidates: check keys case-insensitively
                      if (!customValue && c.customFields && typeof c.customFields === 'object') {
                        const targetLabel = (label || '').trim().toLowerCase();
                        const keyMatch = Object.keys(c.customFields).find(k => k.trim().toLowerCase() === targetLabel);
                        if (keyMatch) {
                          customValue = c.customFields[keyMatch];
                        }
                      }

                      // Pre-mapped field types (dob, age, gender) are saved to native top-level
                      // candidate fields by the server, not inside customFields.
                      if (!customValue && customFieldDef?.fieldType) {
                        const ft = customFieldDef.fieldType;
                        if (ft === 'dob' && c.dob) {
                          const d = new Date(c.dob);
                          if (!isNaN(d.getTime())) {
                            const dd = String(d.getUTCDate()).padStart(2, '0');
                            const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
                            const yyyy = d.getUTCFullYear();
                            customValue = `${dd}/${mm}/${yyyy}`;
                          }
                        } else if (ft === 'age') {
                          const ageVal = c.ageYears != null ? c.ageYears : c.age;
                          if (ageVal != null) customValue = String(ageVal);
                        } else if (ft === 'gender') {
                          if (c.gender) customValue = c.gender;
                        }
                      }

                      return (
                        <td key={colId} className="px-4 py-4 text-sm text-gray-700 text-center whitespace-nowrap table-cell">
                          {customValue || '—'}
                        </td>
                      );
                    }
                    switch (colId) {
                      case 'status':
                        return showStatus ? (
                          <td key={colId} className="px-3 py-3 whitespace-nowrap text-center table-cell">
                            {c.pendingReuploadDocuments?.length > 0 && !c.rejected ? (
                              // HR rejected documents the candidate has not re-uploaded. Shown in
                              // every tab, Selected included; the candidate's tab never changes.
                              <UITooltipProvider>
                                <UITooltip>
                                  <UITooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200 cursor-help">
                                      Pending Documents
                                      <InformationCircleIcon className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                    </span>
                                  </UITooltipTrigger>
                                  <UITooltipContent side="top" align="center" avoidCollisions className="max-w-xs p-3">
                                    <div className="text-xs space-y-1.5 leading-relaxed text-left font-normal">
                                      <p className="font-semibold text-gray-900">Rejected documents pending re-upload</p>
                                      <ul className="list-disc list-inside text-gray-600 space-y-0.5 text-[11px]">
                                        {c.pendingReuploadDocuments.map((label) => (
                                          <li key={label}>{label}</li>
                                        ))}
                                      </ul>
                                      {c.shortlisted && (
                                        <p className="text-[11px] text-gray-500 pt-1 border-t border-gray-100">
                                          {isSamtaGroup ? 'Shortlisted' : 'Selected'} candidate. The decision is unchanged.
                                        </p>
                                      )}
                                    </div>
                                  </UITooltipContent>
                                </UITooltip>
                              </UITooltipProvider>
                            ) : screeningMeta?.qualifyingOnly && !c.rejected && !c.shortlisted ? (
                              // A qualifying-only screening: where the candidate is in the
                              // Qualifying Agent, and a click opens their answers.
                              <QualifyingAgentBoundary surface="candidates-table status">
                                <QualifyingStageChip
                                  qualifying={c.qualifying}
                                  onOpen={
                                    c.qualifying && typeof openQualifyingAnswersForCandidate === 'function'
                                      ? () => openQualifyingAnswersForCandidate(c)
                                      : undefined
                                  }
                                />
                              </QualifyingAgentBoundary>
                            ) : (c.isLocalTimeout) || (c.isInterviewIncomplete && c.status !== 'In Progress' && !c.isProcessingScores) ? (
                              <UITooltipProvider>
                                <UITooltip>
                                  <UITooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-red-50 text-red-700 ring-1 ring-red-100 cursor-help">
                                      Not Completed
                                      <InformationCircleIcon className="h-3.5 w-3.5 text-red-500 shrink-0" />
                                    </span>
                                  </UITooltipTrigger>
                                  <UITooltipContent side="top" align="center" avoidCollisions className="max-w-xs p-3">
                                    <div className="text-xs space-y-1.5 leading-relaxed text-left font-normal">
                                      <p className="font-semibold text-gray-900">AI Interview Not Completed</p>
                                      <p className="text-gray-600">Possible reasons candidate dropped:</p>
                                      <ul className="list-disc list-inside text-gray-600 space-y-0.5 text-[11px]">
                                        <li>Dropped before starting or during session</li>
                                        <li>Internet/connection or device issues</li>
                                        <li>Scheduling conflict or travelling</li>
                                        <li>Browser tab closed before completion</li>
                                      </ul>
                                      <p className="text-[11px] text-emerald-700 font-medium pt-1 border-t border-gray-100">
                                        Candidate has been communicated to re-attempt the interview via their provided channel.
                                      </p>
                                      {c.followUpCount > 0 && (
                                        <p className="text-[11px] text-gray-500 pt-0.5">
                                          Follow-up message sent {c.followUpCount} time(s){c.lastFollowUpSentAt ? ` (last on ${new Date(c.lastFollowUpSentAt).toLocaleDateString()})` : ''}
                                        </p>
                                      )}
                                    </div>
                                  </UITooltipContent>
                                </UITooltip>
                              </UITooltipProvider>
                            ) : c.isAllZero && c.status !== 'In Progress' && c.status !== 'Completed' && !c.isProcessingScores ? (
                              <UITooltipProvider>
                                <UITooltip>
                                  <UITooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-gray-100 text-gray-500 ring-1 ring-gray-200/80 cursor-help">
                                      Not Completed
                                      <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                    </span>
                                  </UITooltipTrigger>
                                  <UITooltipContent side="top" align="center" avoidCollisions className="max-w-xs p-3">
                                    <div className="text-xs space-y-1 leading-relaxed text-left font-normal">
                                      <p className="font-semibold text-gray-900">No Screening Data</p>
                                      <p className="text-gray-600">The candidate did not attempt any step of the screening.</p>
                                      {c.followUpCount > 0 && (
                                        <p className="text-[11px] text-gray-500 pt-1 border-t border-gray-100">
                                          Follow-up message sent {c.followUpCount} time(s){c.lastFollowUpSentAt ? ` (last on ${new Date(c.lastFollowUpSentAt).toLocaleDateString()})` : ''}
                                        </p>
                                      )}
                                    </div>
                                  </UITooltipContent>
                                </UITooltip>
                              </UITooltipProvider>
                            ) : (
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                  c.rejected
                                    ? 'bg-red-50 text-red-700 ring-1 ring-red-100'
                                    : c.shortlisted
                                      ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-100'
                                      : c.status === 'Completed'
                                        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                                        : c.status === 'interested'
                                          ? 'bg-teal-50 text-teal-700 ring-1 ring-teal-100'
                                          : c.status === 'emailed'
                                            ? 'bg-purple-50 text-purple-700 ring-1 ring-purple-100'
                                            : c.isProcessingScores
                                              ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-100'
                                              : c.status === 'In Progress'
                                                ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-100'
                                                : 'bg-blue-50 text-blue-700 ring-1 ring-blue-100'
                                }`}
                                title={
                                  c.rejected
                                    ? c.rejectedAt
                                      ? `Rejected on ${formatDateTime(c.rejectedAt)}`
                                      : undefined
                                    : c.shortlisted
                                      ? c.shortlistedAt
                                        ? `${isSamtaGroup ? 'Shortlisted' : 'Selected'} on ${formatDateTime(c.shortlistedAt)}`
                                        : undefined
                                      : c.status === 'Completed'
                                        ? c.completedAt
                                          ? `Completed on ${formatDateTime(c.completedAt)}`
                                          : undefined
                                        : c.isProcessingScores
                                          ? 'Interview completed, calculating scores...'
                                          : c.status === 'In Progress'
                                            ? c.screeningStartedAt
                                              ? `Started on ${formatDateTime(c.screeningStartedAt)}`
                                              : undefined
                                            : c.invitedAt
                                              ? `Invited on ${formatDateTime(c.invitedAt)}`
                                              : undefined
                                }
                              >
                                {c.rejected ? 'Rejected' : c.shortlisted ? (isSamtaGroup ? 'Shortlisted' : 'Selected') : c.isProcessingScores ? (
                                  <>
                                    <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-amber-300 border-t-amber-600" aria-hidden />
                                    In Progress
                                  </>
                                ) : (c.status ? (c.status.charAt(0).toUpperCase() + c.status.slice(1)) : 'Invited')}
                              </span>
                            )}
                          </td>
                        ) : null;

              case 'decision':
                return showDecisionColumn ? (
                  <td key={colId} className="px-4 py-3 whitespace-nowrap text-center table-cell w-[120px]" onClick={(e) => e.stopPropagation()}>
                    <div className="relative inline-flex justify-center w-full">
                      <button
                        type="button"
                        aria-label="Decision dropdown"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (openDecisionDropdownId === candidateId) {
                            setOpenDecisionDropdownId(null);
                            setDecisionDropdownPosition(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setDecisionDropdownPosition({ top: rect.bottom + 4, left: rect.left + rect.width / 2 });
                            setOpenDecisionDropdownId(candidateId);
                          }
                        }}
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium border transition-colors ${getDecisionOption(shortlistDecisions[candidateId])
                            ? `${getDecisionOption(shortlistDecisions[candidateId]).classes}`
                            : 'bg-gray-100 text-gray-400 border-gray-200 hover:bg-gray-200'
                          }`}
                      >
                        <span>{getDecisionOption(shortlistDecisions[candidateId])?.label || '—'}</span>
                        <ChevronDownIcon className="h-2.5 w-2.5 shrink-0" />
                      </button>
                      {openDecisionDropdownId === candidateId && decisionDropdownPosition && createPortal(
                        <div
                          className="fixed z-[9999] min-w-[150px] bg-white rounded-lg shadow-lg border border-gray-200 py-1 overflow-hidden"
                          style={{ top: decisionDropdownPosition.top, left: decisionDropdownPosition.left, transform: 'translateX(-50%)' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={async () => {
                              setOpenDecisionDropdownId(null);
                              setDecisionDropdownPosition(null);
                              const ok = await patchCandidateShortlistField(candidateId, { shortlistDecision: null });
                              if (ok !== false) {
                                setShortlistDecisions((prev) => ({ ...prev, [candidateId]: null }));
                              }
                            }}
                            className="flex items-center w-full px-3 py-2 text-xs text-gray-500 hover:bg-gray-50"
                          >
                            No decision
                          </button>
                          {DECISION_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              data-testid={`public-decision-${opt.value}`}
                              onClick={async () => {
                                setOpenDecisionDropdownId(null);
                                setDecisionDropdownPosition(null);
                                const ok = await patchCandidateShortlistField(candidateId, {
                                  shortlistDecision: opt.value,
                                });
                                if (ok !== false) {
                                  setShortlistDecisions((prev) => ({ ...prev, [candidateId]: opt.value }));
                                }
                              }}
                              className={`flex items-center w-full px-3 py-2 text-xs font-medium hover:bg-gray-50 ${opt.value === shortlistDecisions[candidateId] ? 'bg-gray-50' : ''}`}
                            >
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${opt.classes}`}>
                                {opt.label}
                              </span>
                            </button>
                          ))}
                        </div>,
                        document.body
                      )}
                    </div>
                  </td>
                ) : null;

              case 'notes':
                return showNotesColumn ? (
                  <td key={colId} className="px-4 py-3 table-cell text-center" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          setOpenNotesModalName(candidateFullName);
                          setOpenNotesModalId(candidateId);
                        }}
                        className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium border transition-colors ${shortlistNotes[candidateId]
                            ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                            : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        <DocumentTextIcon className="h-3.5 w-3.5" />
                        {shortlistNotes[candidateId] ? 'View/Edit' : 'Add Note'}
                      </button>
                    </div>
                  </td>
                ) : null;

                                    case 'resumeScore':
                    return showResumeScoreColumn ? (

                    <React.Fragment key={colId}>

                      {/* resume score */}

                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell">

                        {c.status === 'In Progress' && c.resumeScore == null ? (

                          // In progress with nothing scored yet — dash. If the resume HAS been scored,
                          // fall through and show it even while the rest of the screening continues.

                          <span className="text-gray-400">—</span>

                        ) : isResumeDropped ? (

                          <UITooltipProvider>

                            <UITooltip>

                              <UITooltipTrigger asChild>

                                <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-gray-500 bg-gray-50 hover:bg-gray-100 border border-gray-200/60 cursor-help font-medium">

                                  0%

                                  <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400" />

                                </span>

                              </UITooltipTrigger>

                              <UITooltipContent side="top" align="center" avoidCollisions>

                                <div className="text-xs max-w-xs leading-relaxed text-left font-normal">

                                  {c.screeningForceCompleteReason === 'timer_expired'

                                    ? "The screening timer expired before the candidate uploaded their resume."

                                    : "Candidate dropped from the screening before uploading their resume."}

                                </div>

                              </UITooltipContent>

                            </UITooltip>

                          </UITooltipProvider>

                        ) : c.resumeScore != null ? (
                          <div className="inline-flex items-center gap-1.5 justify-center">
                            <button
                              type="button"
                              data-testid="resume-score-cell"
                              onClick={() => openCandidateResumeBreakdown(c)}
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              title="View score breakdown"
                            >
                              {c.resumeScore}%
                              <EyeIcon className="h-3.5 w-3.5" />
                            </button>
                            {isBurgerKingClient && hasBurgerKingExperience(c) && (
                              <UITooltipProvider delayDuration={200}>
                                <UITooltip>
                                  <UITooltipTrigger asChild>
                                    <span className="text-sm cursor-help hover:scale-110 transition-transform">🍔</span>
                                  </UITooltipTrigger>
                                  <UITooltipContent side="top" align="center" avoidCollisions>
                                    <div className="text-xs max-w-[200px] leading-relaxed text-center font-normal">
                                      The candidate may have previously worked at Burger King. Open profile to get more information.
                                    </div>
                                  </UITooltipContent>
                                </UITooltip>
                              </UITooltipProvider>
                            )}
                            {/* {documentStatusMap[c._id] && (documentStatusMap[c._id].status === 'partial' || documentStatusMap[c._id].status === 'submitted') && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (typeof onViewDocumentsCandidate === 'function') {
                                    onViewDocumentsCandidate(c);
                                  }
                                }}
                                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                title="View uploaded documents"
                              >
                                <DocumentTextIcon className="h-3.5 w-3.5" />
                                <EyeIcon className="h-3 w-3" />
                              </button>
                            )} */}
                          </div>

                        ) : isResumeExtractionFailed(c) ? (

                          // Resume was uploaded but could not be read (bad/empty/unreadable file), so no
                          // AI score was generated. Show an openable "Failed" chip instead of a silent dash
                          // so recruiters can open the insight modal to read why and preview the original file.
                          <button

                            type="button"

                            onClick={() => openCandidateResumeBreakdown(c)}

                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-rose-700 bg-rose-50 hover:bg-rose-100 focus:outline-none focus:ring-1 focus:ring-rose-500 font-medium"

                            title="Resume could not be read — click to view details and preview the file"

                          >

                            <ExclamationTriangleIcon className="h-3.5 w-3.5" />

                            Failed

                          </button>

                        ) : (

                          <span className="text-gray-400">—</span>

                        )}

                      </td>

                    </React.Fragment>

                  ) : null;

                  case 'duplicates':
                    return resolvedShowDuplicateColumn ? (
                      <td key={colId} className="px-3 py-3 text-center align-middle">
                        {c?.duplicateSummary?.count > 0 ? (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              // Guarded because this table also renders on the public
                              // hiring-manager review page, which passes no recruiter
                              // callbacks. Every other cell-level callback here is
                              // already typeof-checked; this one was not, so a saved
                              // column layout with `duplicates: true` would crash that
                              // page on click (same failure as the setActionMenuPosition
                              // regression that hideRowActions was added for).
                              if (typeof setDuplicateModalCandidate === 'function') {
                                setDuplicateModalCandidate(c);
                              }
                            }}
                            className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50/80 px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
                          >
                            <InformationCircleIcon className="h-4 w-4" />
                            <span>
                              {c?.duplicateSummary?.count} time{c?.duplicateSummary?.count > 1 ? 's' : ''}
                            </span>
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm"></span>
                        )}
                      </td>
                    ) : null;

                  
                  case 'assessmentModulesBlock':
                    return (
                      <AssessmentModuleScoreCells
                        key="assessmentModulesBlock"
                        candidate={c}
                        modules={visibleAssessmentModuleColumns}
                        screeningMeta={screeningMeta}
                        onViewModuleScore={openCandidateResumeBreakdown}
                        isAssessmentDropped={c.assessmentScore === 0 && !c.hasAssessmentScoreDetail}
                        isRawScoreClient={user?.orgSlug === 'ajantapharma-80unds'}
                      />
                    );
                  case 'overallScore':
                    return showOverallScoreColumn ? (

                    <React.Fragment key={colId}>

                      {/* overall score – average of available scores; requires 2+ components */}

                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">

                        {c.status === 'In Progress' && !hasAnyStageScore && (screeningMeta?.hasResumeScoring !== false || screeningMeta?.includeAssessment !== false || screeningMeta?.includeInterview !== false || screeningMeta?.isMovedCandidatesPool) ? (

                          <span

                            className="inline-flex items-center justify-center text-gray-400"

                            role="status"

                            aria-label="Calculating overall score"

                          >

                            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600" aria-hidden />

                          </span>

                        ) : (

                          (() => {

                            const scoresObj = {};
                            if (!isAssessmentOnly && !isInterviewOnly && !isAssessmentInterviewDual) {
                              scoresObj.resume = c.resumeScore;
                            }
                            if (!isResumeOnly && !isInterviewOnly && !isResumeInterviewDual) {
                              scoresObj.assessment = getAssessmentPctForRow(c);
                            }
                            if (!isResumeOnly && !isAssessmentOnly && !isResumeAssessmentDual) {
                              scoresObj.interview = c.interviewScore;
                            }

                            const overallScore = calculateWeightedOverallScore(scoresObj, weightsConfig);
                            if (overallScore == null) return <span className="text-gray-400">—</span>;



                        const totalAssessmentPts = c.assessmentTotalPoints || screeningMeta?.assessmentTotalPoints || screeningMeta?.assessment?.totalPoints;

                            const hasAssessmentRaw =

                          showAssessment && c.assessmentScore != null && totalAssessmentPts;

                            const overallScoreEl = (
                              <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                                {`${overallScore}%`}
                                {hasAnyDroppedStage && (
                                  <InformationCircleIcon className="h-3.5 w-3.5 text-indigo-500/80" />
                                )}
                              </span>
                            );

                            if (hasAnyDroppedStage) {
                              return (
                                <UITooltipProvider>
                                  <UITooltip>
                                    <UITooltipTrigger asChild>
                                      <button type="button" className="focus:outline-none cursor-help">
                                        {overallScoreEl}
                                      </button>
                                    </UITooltipTrigger>
                                    <UITooltipContent side="top" align="center" avoidCollisions>
                                      <div className="text-xs max-w-xs leading-relaxed font-normal text-left">
                                        This overall score includes 0% for one or more incomplete stages after the candidate dropped from the screening.
                                      </div>
                                    </UITooltipContent>
                                  </UITooltip>
                                </UITooltipProvider>
                              );
                            }

                            if (

                              !isDualMethodLayout &&

                              hasAssessmentRaw &&

                              (screeningMeta.assessmentId || c.assessmentId)

                            ) {

                              return (

                                <button

                                  type="button"

                                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"

                                  title="Overall Score is average of Resume Score, Assessment Score and Interview score"

                                >

                                  {`${overallScore}%`}

                                  {/* <EyeIcon className="h-3.5 w-3.5" /> */}

                                </button>

                              );

                            }



                            return overallScoreEl;

                          })()

                        )}

                      </td>

                    </React.Fragment>

                  ) : null;

                  case 'assessmentScore':
                    return showAssessmentScoreColumn ? (

                    <React.Fragment key={colId}>

                      {/* assessment score – only when screening has assessment */}

                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell">

                        {/* Personality never has a %, so it must be checked before the
                            "In Progress with no %" dash below or a finished profile is hidden. */}
                        {c.status === 'In Progress' && rowAssessmentPct == null && !isPersonalityResult(c.personalityResult) ? (

                          <span className="text-gray-400">—</span>

                        ) : isAssessmentDropped && !isPersonalityResult(c.personalityResult) ? (

                          <UITooltipProvider>

                            <UITooltip>

                              <UITooltipTrigger asChild>

                                <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-gray-500 bg-gray-50 hover:bg-gray-100 border border-gray-200/60 cursor-help font-medium">

                                  0%

                                  <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400" />

                                </span>

                              </UITooltipTrigger>

                              <UITooltipContent side="top" align="center" avoidCollisions>

                                <div className="text-xs max-w-xs leading-relaxed text-left font-normal">

                                  {c.screeningForceCompleteReason === 'timer_expired'

                                    ? "The screening timer expired before the candidate completed the assessment."

                                    : "Candidate dropped from the screening before completing the assessment."}

                                </div>

                              </UITooltipContent>

                            </UITooltip>

                          </UITooltipProvider>

                        ) : isPersonalityResult(c.personalityResult) ? (

                          // Personality (IPIP-50): raw trait scores, never a % (kept out of Overall Score)
                          <button
                            type="button"
                            data-testid="assessment-personality-cell"
                            onClick={() => handleInlineViewAssessmentScore(c)}
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 tabular-nums text-xs"
                            title={`Work style profile: ${describePersonality(c.personalityResult)}`}
                          >
                            {formatPersonalityChip(c.personalityResult)}
                            {c.personalityResult?.quality?.lowConfidence && (
                              <ExclamationTriangleIcon className="h-3.5 w-3.5 text-amber-600" />
                            )}
                            <EyeIcon className="h-3.5 w-3.5" />
                          </button>

                        ) : c.assessmentScore != null ? (

                          <button

                            type="button"

                            data-testid="assessment-score-cell"

                            onClick={() => handleInlineViewAssessmentScore(c)}

                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"

                            title="View assessment score and proctoring details"

                          >

                            {(() => {

                              const score = c.assessmentScore;

                          const totalPoints = c.assessmentTotalPoints || screeningMeta?.assessmentTotalPoints || screeningMeta?.assessment?.totalPoints;
                              const isRawScoreClient = user?.orgSlug === 'ajantapharma-80unds';

                              if (isRawScoreClient) {
                                const roundedScore = Math.round(score * 10) / 10;
                                return totalPoints ? `${roundedScore}/${totalPoints}` : `${roundedScore}`;
                              }

                              if (totalPoints && totalPoints > 0) {

                                const percentage = Math.round((score / totalPoints) * 100);

                                return `${percentage}%`;

                              }

                              return `${score}%`;

                            })()}

                            <EyeIcon className="h-3.5 w-3.5" />

                          </button>

                        ) : (

                          <span className="text-gray-400">—</span>

                        )}

                      </td>

                    </React.Fragment>

                  ) : null;

                  case 'interviewScore':
                    return showInterviewScoreColumn ? (

                    <React.Fragment key={colId}>

                      {/* interview score */}

                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell">

                        {c.status === 'In Progress' && c.interviewScore == null ? (

                          <span className="text-gray-400">—</span>

                        ) : isInterviewDropped ? (

                          <UITooltipProvider>

                            <UITooltip>

                              <UITooltipTrigger asChild>

                                <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-gray-500 bg-gray-50 hover:bg-gray-100 border border-gray-200/60 cursor-help font-medium">

                                  0%

                                  <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400" />

                                </span>

                              </UITooltipTrigger>

                              <UITooltipContent side="top" align="center" avoidCollisions>

                                <div className="text-xs max-w-xs leading-relaxed text-left font-normal">

                                  {(() => {
                                    switch (c.interviewDropReason) {
                                      case 'timer_expired':
                                        return 'The screening timer expired before the candidate could finish the interview.';
                                      case 'timer_expired_never_started':
                                        return 'Candidate did not attempt the interview.';
                                      case 'candidate_left':
                                        return 'The candidate left or disconnected during the interview.';
                                      case 'candidate_no_show':
                                        return 'The candidate did not join the interview session.';
                                      case 'candidate_dropped':
                                        return 'The candidate dropped out during the interview before it was completed.';
                                      case 'never_started':
                                        return 'The candidate did not start the interview.';
                                      case 'connection_lost':
                                        return 'The interview was interrupted due to a connection issue.';
                                      case 'agent_not_joined':
                                        return 'The AI interviewer could not connect to the session. Please retry or contact support.';
                                      case 'session_timed_out':
                                        return 'The interview session became inactive and was automatically closed.';
                                      case 'session_conflict':
                                        return 'The interview session was replaced by a newer session.';
                                      case 'stt_failure':
                                        return "The candidate's audio could not be transcribed. A recording may be available for manual review.";
                                      case 'no_transcript':
                                        return 'No interview responses were captured. The session may not have started properly.';
                                      case 'scoring_failed':
                                        return 'The interview was completed but scoring could not be processed. A retry is in progress.';
                                      case 'still_in_progress':
                                        return 'The interview session is still in progress.';
                                      default:
                                        return c.screeningForceCompleteReason === 'timer_expired'
                                          ? 'The screening timer expired before the candidate completed the interview.'
                                          : 'The candidate did not complete the interview.';
                                    }
                                  })()}

                                </div>

                              </UITooltipContent>

                            </UITooltip>

                          </UITooltipProvider>

                        ) : c.interviewScore != null && c.interviewSessionId ? (

                          <button

                            type="button"

                            data-testid="interview-score-cell"

                            onClick={() => openInterviewSessionForCandidate(c)}

                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"

                            title="View interview replay and analysis"

                          >

                            {c.interviewScore}%

                            <EyeIcon className="h-3.5 w-3.5" />

                          </button>

                        ) : c.interviewScore != null && screeningMeta?.includeWhatsAppAgent ? (

                          <button

                            type="button"

                            data-testid="whatsapp-interview-score-cell"

                            onClick={() => openWhatsAppSessionForCandidate(c)}

                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[#1c9950] bg-[#25D366]/10 hover:bg-[#25D366]/20 focus:outline-none focus:ring-1 focus:ring-[#25D366]"

                            title="View WhatsApp interview questions and answers"

                          >

                            {c.interviewScore}%

                            <EyeIcon className="h-3.5 w-3.5" />

                          </button>

                        ) : c.interviewScore != null ? (

                          <button

                            type="button"

                            data-testid="interview-score-cell"

                            onClick={() => openInterviewSessionForCandidate(c)}

                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"

                            title="Open interview replay and analysis"

                          >

                            {c.interviewScore}%

                            <EyeIcon className="h-3.5 w-3.5" />

                          </button>

                        ) : (

                          <span className="text-gray-400">—</span>

                        )}

                      </td>

                    </React.Fragment>

                  ) : null;

                  case 'shortlistedDate':
                    return showShortlistedDateEffective ? (

                    <td key={colId} className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap table-cell text-center">

                      {formatShortlistedDate(c.shortlistedAt)}

                    </td>

                  ) : null;

                  case 'rejectedDate':
                    return showRejectedDateEffective ? (

                    <td key={colId} className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap table-cell text-center">

                      {formatRejectedDate(c.rejectedAt)}

                    </td>

                  ) : null;

                  case 'proctoring':
                    return showProctoringColumn ? (

                    <React.Fragment key={colId}>

                      {/* combined proctoring flag for assessment + interview */}

                      <td className="px-3 py-3 whitespace-nowrap text-sm text-center table-cell">

                        {c.status === 'In Progress' &&
                        c.assessmentProctoringRiskScore == null &&
                        c.interviewProctoringRiskScore == null ? (

                          // No proctoring result recorded yet — dash. Once either stage has been
                          // proctored, show the flag even if the screening is still in progress.

                          <span className="text-gray-400">—</span>

                        ) : (

                          (() => {

                            const hasAssessmentProctoring =

                              showAssessment &&

                              c.assessmentScore != null &&

                              (screeningMeta.assessmentId || c.assessmentId);

                            const hasInterviewProctoring = !!(c.interviewSessionId && screeningMeta.hasInterview);



                            // Tooltip rows follow screening configuration (not every method is always enabled).

                            const proctoringTooltipShowAssessment =

                              screeningMeta?.includeAssessment !== false;

                            const proctoringTooltipShowInterview =

                              screeningMeta?.includeInterview !== false;



                            if (!hasAssessmentProctoring && !hasInterviewProctoring) {

                              return <span className="text-gray-400">—</span>;

                            }



                            const assessmentRiskScore = c.assessmentProctoringRiskScore;

                            const interviewRiskScore = c.interviewProctoringRiskScore;

                            const combinedRiskScore = c.proctoringRiskScore;



                            const combinedBadge = getCombinedScreeningProctoringBadge(
                              interviewRiskScore,
                              assessmentRiskScore
                            );

                            const assessmentBadge = getRiskBadgeFromScore(assessmentRiskScore);

                            const interviewBadge = getInterviewProctoringRiskBadgeFromScore(interviewRiskScore);



                            const overallLabel = combinedBadge.label;

                            const overallClasses = combinedBadge.classes;

                            const combinedShownScore =
                              combinedRiskScore != null ? Math.floor(Number(combinedRiskScore)) : null;
                            const interviewShownScore =
                              interviewRiskScore != null ? Math.floor(Number(interviewRiskScore)) : null;



                            const handleClick = async (e) => {

                              e.stopPropagation();

                              const hasAssessmentScore = hasAssessmentProctoring && assessmentRiskScore != null;

                              const hasInterviewScore = hasInterviewProctoring && interviewRiskScore != null;



                              // Decide primary destination based on higher risk score

                              if (hasAssessmentScore || hasInterviewScore) {

                                const a = hasAssessmentScore ? Number(assessmentRiskScore) : 0;

                                const i = hasInterviewScore ? Number(interviewRiskScore) : 0;



                                if (i >= a && hasInterviewProctoring) {

                                  await openInterviewSessionForCandidate(c, 'proctoring');

                                  return;

                                }

                                if (hasAssessmentProctoring) {

                                  await handleInlineViewAssessmentProctoring(c);

                                  return;

                                }

                              } else {

                                // Fallback to whichever is available

                                if (hasAssessmentProctoring) {

                                  await handleInlineViewAssessmentProctoring(c);

                                  return;

                                }

                                if (hasInterviewProctoring) {

                                  await openInterviewSessionForCandidate(c, 'proctoring');

                                  return;

                                }

                              }

                            };



                            return (

                              <UITooltipProvider>

                                <UITooltip>

                                  <UITooltipTrigger asChild>

                                    <button

                                      type="button"

                                      onClick={handleClick}

                                      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium border ${overallClasses}`}

                                      title={overallLabel}

                                    >

                                      {overallLabel === 'Compliant' ? (

                                        <CheckCircleIcon className="h-4 w-4" />

                                      ) : overallLabel === 'Flagged' ? (

                                        <ExclamationTriangleIcon className="h-4 w-4" />

                                      ) : overallLabel === 'High Risk' ? (

                                        <XMarkIcon className="h-4 w-4" />

                                      ) : (

                                        overallLabel

                                      )}

                                      {combinedRiskScore != null && (

                                        <span>

                                          {combinedShownScore >= 100

                                            ? `${combinedRiskScore}+`

                                            : `${combinedRiskScore}`}

                                        </span>

                                      )}

                                    </button>

                                  </UITooltipTrigger>

                                  <UITooltipContent side="top">

                                    <div className="text-xs space-y-1">

                                      <div className="flex flex-col gap-1">

                                        {proctoringTooltipShowAssessment && (

                                          <div className="flex items-center gap-1.5">

                                            <span className="text-[11px] font-semibold text-gray-600">

                                              Assessment:

                                            </span>

                                            {hasAssessmentProctoring && assessmentRiskScore != null ? (

                                              <button

                                                type="button"

                                                onClick={async (e) => {

                                                  e.stopPropagation();

                                                  await handleInlineViewAssessmentProctoring(c);

                                                }}

                                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${assessmentBadge.classes}`}

                                                title={assessmentBadge.label}

                                              >

                                                {assessmentBadge.label === 'Compliant' ? (

                                                  <CheckCircleIcon className="h-3.5 w-3.5" />

                                                ) : assessmentBadge.label === 'Flagged' ? (

                                                  <ExclamationTriangleIcon className="h-3.5 w-3.5" />

                                                ) : assessmentBadge.label === 'High Risk' ? (

                                                  <XMarkIcon className="h-3.5 w-3.5" />

                                                ) : (

                                                  assessmentBadge.label

                                                )}

                                                <span>

                                                  {assessmentRiskScore > 100

                                                    ? `${assessmentRiskScore}+`

                                                    : `${assessmentRiskScore}`}

                                                </span>

                                              </button>

                                            ) : (

                                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-gray-100 text-gray-500">

                                                No data

                                              </span>

                                            )}

                                          </div>

                                        )}



                                        {proctoringTooltipShowInterview && (

                                          <div className="flex items-center gap-1.5">

                                            <span className="text-[11px] font-semibold text-gray-600">

                                              Interview:

                                            </span>

                                            {hasInterviewProctoring && interviewRiskScore != null ? (

                                              <button

                                                type="button"

                                                onClick={async (e) => {

                                                  e.stopPropagation();

                                                  await openInterviewSessionForCandidate(c, 'proctoring');

                                                }}

                                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${interviewBadge.classes}`}

                                                title={interviewBadge.label}

                                              >

                                                {interviewBadge.label === 'Compliant' ? (

                                                  <CheckCircleIcon className="h-3.5 w-3.5" />

                                                ) : interviewBadge.label === 'Flagged' ? (

                                                  <ExclamationTriangleIcon className="h-3.5 w-3.5" />

                                                ) : interviewBadge.label === 'High Risk' ? (

                                                  <XMarkIcon className="h-3.5 w-3.5" />

                                                ) : (

                                                  interviewBadge.label

                                                )}

                                                <span>

                                                  {interviewShownScore >= 100

                                                    ? `${interviewRiskScore}+`

                                                    : `${interviewRiskScore}`}

                                                </span>

                                              </button>

                                            ) : hasInterviewProctoring ? (

                                              <button

                                                type="button"

                                                onClick={async (e) => {

                                                  e.stopPropagation();

                                                  await openInterviewSessionForCandidate(c, 'proctoring');

                                                }}

                                                className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-blue-50 text-blue-700"

                                              >

                                                View proctoring

                                              </button>

                                            ) : (

                                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-gray-100 text-gray-500">

                                                No data

                                              </span>

                                            )}

                                          </div>

                                        )}

                                      </div>

                                    </div>

                                  </UITooltipContent>

                                </UITooltip>

                              </UITooltipProvider>

                            );

                          })())}

                      </td>

                    </React.Fragment>

                  ) : null;

                  case 'companyName':
                        return (
                          <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[140px] min-w-[140px] max-w-[140px]" title={c.company || ''}>
                            <span className="block truncate">{renderCompanyName(c.company)}</span>
                          </td>
                        );
                  case 'storeId':
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-center table-cell w-[110px] min-w-[110px] max-w-[110px]">
                        <div className="relative">
                          <TableStoreDropdown
                            value={c.storeId || ''}
                            stores={storeOptions}
                            onChange={async (nextVal) => {
                              await patchCandidateShortlistField(c._id || c.id, { storeId: nextVal });
                            }}
                          />
                          {(c.storeId && resolveCandidateStore(c)) && (
                            <UITooltipProvider>
                              <UITooltip>
                                <UITooltipTrigger asChild>
                                  <span className="absolute -top-1 -right-1 flex h-2 w-2 cursor-pointer z-10">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                                  </span>
                                </UITooltipTrigger>
                                <UITooltipContent className="p-3 bg-slate-900 text-white rounded-lg shadow-xl text-xs max-w-xs space-y-1 text-left z-[110]">
                                  {(() => {
                                    const store = resolveCandidateStore(c);
                                    return (
                                      <>
                                        <p className="font-bold border-b border-slate-700 pb-1 mb-1">{store?.name}</p>
                                        <p><span className="text-slate-400">Location:</span> {store?.city}, {store?.state} ({store?.region || ''})</p>
                                        {store?.category && <p><span className="text-slate-400">Category:</span> {store?.category}</p>}
                                        {store?.armName && <p><span className="text-slate-400">ARM:</span> {store?.armName} ({store?.armEmail})</p>}
                                      </>
                                    );
                                  })()}
                                </UITooltipContent>
                              </UITooltip>
                            </UITooltipProvider>
                          )}
                        </div>
                      </td>
                    );
                  case 'storeName': {
                    const val = resolveCandidateStore(c)?.name || '—';
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[140px] min-w-[140px] max-w-[140px]" title={val !== '—' ? val : ''}>
                        <span className="block truncate">{val}</span>
                      </td>
                    );
                  }
                  case 'storeRegion': {
                    const val = resolveCandidateStore(c)?.region || '—';
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[120px] min-w-[120px] max-w-[120px]" title={val !== '—' ? val : ''}>
                        <span className="block truncate">{val}</span>
                      </td>
                    );
                  }
                  case 'storeCity': {
                    const val = resolveCandidateStore(c)?.city || '—';
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[120px] min-w-[120px] max-w-[120px]" title={val !== '—' ? val : ''}>
                        <span className="block truncate">{val}</span>
                      </td>
                    );
                  }
                  case 'storeState': {
                    const val = resolveCandidateStore(c)?.state || '—';
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[120px] min-w-[120px] max-w-[120px]" title={val !== '—' ? val : ''}>
                        <span className="block truncate">{val}</span>
                      </td>
                    );
                  }
                  case 'storeCategory': {
                    const val = resolveCandidateStore(c)?.category || '—';
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[120px] min-w-[120px] max-w-[120px]" title={val !== '—' ? val : ''}>
                        <span className="block truncate">{val}</span>
                      </td>
                    );
                  }
                  case 'armName': {
                    const val = resolveCandidateStore(c)?.armName || '—';
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[140px] min-w-[140px] max-w-[140px]" title={val !== '—' ? val : ''}>
                        <span className="block truncate">{val}</span>
                      </td>
                    );
                  }
                  case 'armEmail': {
                    const val = resolveCandidateStore(c)?.armEmail || '—';
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[160px] min-w-[160px] max-w-[160px]" title={val !== '—' ? val : ''}>
                        <span className="block truncate">{val}</span>
                      </td>
                    );
                  }
                  case 'cityCode':
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[110px] min-w-[110px] max-w-[110px]" title={c.cityId || ''}>
                        <span className="block truncate">{c.cityId || '—'}</span>
                      </td>
                    );
                  case 'cityCity':
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[120px] min-w-[120px] max-w-[120px]" title={c.cityDetails?.city || ''}>
                        <span className="block truncate">{c.cityDetails?.city || '—'}</span>
                      </td>
                    );
                  case 'cityState':
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[120px] min-w-[120px] max-w-[120px]" title={c.cityDetails?.state || ''}>
                        <span className="block truncate">{c.cityDetails?.state || '—'}</span>
                      </td>
                    );
                  case 'cityRegion':
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[120px] min-w-[120px] max-w-[120px]" title={c.cityDetails?.region || ''}>
                        <span className="block truncate">{c.cityDetails?.region || '—'}</span>
                      </td>
                    );
                  case 'currentCtc':
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[140px] min-w-[140px] max-w-[140px]" title={c.currentCtc || ''}>
                        <span className="block truncate">{c.currentCtc ? `${c.currentCtc} LPA` : '—'}</span>
                      </td>
                    );
                  case 'noticePeriod':
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[140px] min-w-[140px] max-w-[140px]" title={c.noticePeriod || ''}>
                        <span className="block truncate">{c.noticePeriod || '—'}</span>
                      </td>
                    );
                      case 'screening':
                        return (
                          <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[170px] min-w-[170px] max-w-[200px]">
                            {c.screening && c.screening._id ? (
                              <div className="flex items-center gap-1">
                                <UITooltipProvider>
                                  <UITooltip>
                                    <UITooltipTrigger asChild>
                                      <span className="block truncate font-medium text-[#007BFF]" title={c.screening.name || 'Screening'}>
                                        {c.screening.name || 'Screening'}
                                      </span>
                                    </UITooltipTrigger>
                                    <UITooltipContent>
                                      <p>{c.screening.name || 'Screening'}</p>
                                    </UITooltipContent>
                                  </UITooltip>
                                </UITooltipProvider>
                                {/* In-app deep link — pointless on the public review
                                    page, where following it just bounces a logged-out
                                    hiring manager to /login. */}
                                {!isPublicView && (
                                  <Link
                                    to={`/dashboard/job-posts/jobs/${c.jobPostingId}/screenings/${c.screening._id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center h-6 w-6 rounded-lg border border-gray-200 text-gray-500 hover:text-[#007BFF] hover:border-[#007BFF]/50 transition-colors"
                                    title="Open screening"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                  </Link>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        );
                      case 'designation':
                        return (
                          <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[150px] min-w-[150px] max-w-[150px]" title={c.currentRole || ''}>
                            <span className="block truncate">{normalizeTitleCaseLowerRemainder(c.currentRole)}</span>
                          </td>
                        );
                      case 'location':
                        return (
                          <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[110px] min-w-[110px] max-w-[110px]" title={c.location || ''}>
                            <span className="block truncate">{c.location || '—'}</span>
                          </td>
                        );
                      case 'industry':
                        return (
                          <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[120px] min-w-[120px] max-w-[120px]" title={formatSimpleList(Array.isArray(c.industries) ? c.industries : c.industry ? [c.industry] : [])}>
                            <span className="block truncate">{formatSimpleList(Array.isArray(c.industries) ? c.industries : c.industry ? [c.industry] : [])}</span>
                          </td>
                        );
                      case 'experience':
                        return (
                          <td key={colId} className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell">
                            {formatExperienceYearsDisplayWithUnit(
                              c.experienceYears != null ? c.experienceYears : c.experience
                            )}
                          </td>
                        );
                  case 'experienceYears':
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-center table-cell w-[110px] min-w-[110px] max-w-[110px]">
                        {c.experienceYears != null ? `${c.experienceYears} y` : '—'}
                      </td>
                    );
                  case 'age':
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-center table-cell w-[80px] min-w-[80px] max-w-[80px]">
                        {(() => {
                          let ageVal = c.ageYears != null ? c.ageYears : c.age;
                          if (ageVal == null) {
                            // Find dob from native field or custom fields
                            let dobStr = c.dob;
                            if (!dobStr && c.customFields) {
                              const dobKey = Object.keys(c.customFields).find(k => k.toLowerCase() === 'dob' || k.toLowerCase().includes('date of birth'));
                              if (dobKey) dobStr = c.customFields[dobKey];
                            }
                            if (dobStr) {
                              // try parsing dd/mm/yyyy or native Date
                              let d;
                              if (typeof dobStr === 'string' && dobStr.includes('/')) {
                                const parts = dobStr.split('/');
                                if (parts.length === 3) d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00Z`);
                                else d = new Date(dobStr);
                              } else {
                                d = new Date(dobStr);
                              }
                              if (d && !isNaN(d.getTime())) {
                                const ageDifMs = Date.now() - d.getTime();
                                const ageDate = new Date(ageDifMs);
                                ageVal = Math.abs(ageDate.getUTCFullYear() - 1970);
                              }
                            }
                          }
                          return ageVal != null ? ageVal : '—';
                        })()}
                      </td>
                    );
                  case 'gender':
                    return (
                      <td key={colId} className="px-3 py-3 text-sm text-center table-cell w-[80px] min-w-[80px] max-w-[80px] capitalize">
                        {c.gender || '—'}
                      </td>
                    );
                      case 'latestEducation':
                        return (
                          <td key={colId} className="px-3 py-3 text-sm text-center table-cell w-[100px] min-w-[100px] max-w-[100px]" title={c.education || '—'}>
                            <span className="block truncate">{formatLatestEducationAbbrev(c.education)}</span>
                          </td>
                        );
                      case 'skills':
                        return (
                          <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[150px] min-w-[150px] max-w-[150px]" title={formatSimpleList(c.skills)}>
                            <span className="block truncate">{formatSimpleList(c.skills)}</span>
                          </td>
                        );
                      case 'email':
                        return (
                          <td key={colId} className="px-3 py-3 text-sm text-left table-cell w-[180px] min-w-[180px] max-w-[180px]" title={c.email || ''}>
                            <span className="block truncate">{c.email || '—'}</span>
                          </td>
                        );
                      case 'phone':
                        return (
                          <td key={colId} className="px-3 py-3 text-sm text-center table-cell w-[150px] min-w-0 max-w-[150px] align-middle" title={c.phone || ''}>
                            <span className="block max-w-full break-words leading-snug">{c.phone || '—'}</span>
                          </td>
                        );
                      case 'social':
                        return (
                          <td key={colId} className="px-3 py-3 text-sm text-center table-cell w-[130px] min-w-[130px] max-w-[130px]">
                            <SocialLinksCell candidate={c} />
                          </td>
                        );
                      case 'appliedDate':
                        return (
                          <td key={colId} className="px-4 py-3 whitespace-nowrap text-center table-cell text-sm text-gray-700">
                            {formatAppliedDate(c.appliedAt || c.invitedAt || c.createdAt)}
                          </td>
                        );
                      case 'completedAt':
                        return (
                          <td key={colId} className="px-4 py-3 whitespace-nowrap text-center table-cell" onClick={(e) => e.stopPropagation()}>
                            {c.status === 'Completed' && c.completedAt ? (
                              <span className="text-sm text-gray-700">
                                {`${new Date(c.completedAt).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true,
                                })}`}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        );
                      case 'downloaded': {
                        const download = c.payrollDownload;
                        const downloadedTitle = download?.at
                          ? `Downloaded${download.byName ? ` by ${download.byName}` : ''} on ${new Date(download.at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}${download.count > 1 ? ` (${download.count} times)` : ''}`
                          : 'Not downloaded yet';
                        // No salary request ("—", selected before the ARM email existed): nothing to download.
                        const hasSalaryRequest = !!salaryRequestsMap?.[c.id || c._id];
                        return (
                          <td key={colId} className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell" onClick={(e) => e.stopPropagation()}>
                            {salaryRequestsMap && !hasSalaryRequest && !download?.at ? (
                              <span className="text-gray-400">—</span>
                            ) : hasSalaryRequest && typeof onSetPayrollDownloaded === 'function' ? (
                              <PayrollDownloadedSelect
                                downloaded={!!download?.at}
                                title={`${downloadedTitle}. Click to change.`}
                                onChange={(v) => onSetPayrollDownloaded(c, v)}
                              />
                            ) : download?.at ? (
                              <span
                                data-testid="payroll-downloaded-chip"
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default"
                                title={downloadedTitle}
                              >
                                <CheckCircleIcon className="w-3.5 h-3.5 shrink-0" />
                                Yes
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-500 border border-gray-200 cursor-default"
                                title="Not downloaded yet. Select the candidate and use Next Steps → Generate Report, or Download → Filled on the job page."
                              >
                                No
                              </span>
                            )}
                          </td>
                        );
                      }
                      case 'salaryDetails':
                        return showSalaryDetailsColumn ? (
                          <td key={colId} className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell" onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const candidateId = c.id || c._id;
                              const salaryData = salaryRequestsMap?.[candidateId];
                              const isCompleted = salaryData?.status === 'completed';
                              const isPending = salaryData?.status === 'pending';

                              const resolvedDesig = resolveGoColorsDesignation(salaryData, screeningMeta) || 'Filled';

                              if (isCompleted) {
                                return (
                                  <button
                                    type="button"
                                    onClick={() => typeof onOpenSalaryDetailsModal === 'function' && onOpenSalaryDetailsModal(c, salaryData)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-900 border border-emerald-200 shadow-2xs transition cursor-pointer group"
                                    title={`Click to view details:\nDesignation: ${resolvedDesig !== 'Filled' ? resolvedDesig : '—'}\nDOJ: ${salaryData.doj ? new Date(salaryData.doj).toLocaleDateString() : '—'}\nGross Salary: ${salaryData.grossSalary || '—'}`}
                                  >
                                    <EyeIcon className="w-3.5 h-3.5 text-emerald-600 group-hover:scale-110 transition-transform shrink-0" />
                                    <span>Filled</span>
                                  </button>
                                );
                              }

                              if (isPending) {
                                return (
                                  <button
                                    type="button"
                                    onClick={() => typeof onOpenSalaryDetailsModal === 'function' && onOpenSalaryDetailsModal(c, salaryData)}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 shadow-2xs transition cursor-pointer group"
                                    title={`Awaiting submission from Regional Head (${salaryData.sentTo || 'HRBP'}). Click to view or re-send link.`}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
                                    <span>Pending</span>
                                    <EyeIcon className="w-3 h-3 text-amber-500 opacity-80 group-hover:opacity-100 shrink-0" />
                                  </button>
                                );
                              }

                              return (
                                <button
                                  type="button"
                                  onClick={() => typeof onOpenSalaryDetailsModal === 'function' && onOpenSalaryDetailsModal(c, null)}
                                  className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 px-2 py-0.5 rounded-md text-xs transition cursor-pointer"
                                  title="Click to request salary details from Regional Head"
                                >
                                  <EyeIcon className="w-3 h-3 text-slate-400" />
                                  <span>—</span>
                                </button>
                              );
                            })()}
                          </td>
                        ) : null;
                      case 'documents':
                        return resolvedShowDocumentsColumn ? (
                          <td key={colId} className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell" onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const docEntry = documentStatusMap?.[c._id];
                              const st = docEntry?.status;
                              const uploaded = docEntry?.uploaded ?? 0;
                              const total = docEntry?.total ?? 0;
                              if (st === 'submitted' || st === 'partial' || uploaded > 0) {
                                return (
                                  <button
                                    type="button"
                                    onClick={() => typeof onViewDocumentsCandidate === 'function' && onViewDocumentsCandidate(c)}
                                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    title="View documents"
                                  >
                                    {`${uploaded}/${total}`}
                                    <EyeIcon className="h-3.5 w-3.5" />
                                  </button>
                                );
                              }
                              return <span className="text-gray-400">—</span>;
                            })()}
                          </td>
                        ) : null;
                      case 'qualifyingCall':
                        return resolvedShowQualifyingCallColumn ? (
                          <td key={colId} className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell" onClick={(e) => e.stopPropagation()}>
                            <QualifyingAgentBoundary surface="candidates-table cell">
                              {/* One primary value per cell — the score, with the verdict under
                                  it. Turn counts, per-channel chips and the answered count moved
                                  into the tooltip and the replay modal, which already show them.
                                  All of it lives in QualifyingCell so this stage owns its own
                                  presentation and the table keeps exactly one line of it. */}
                              <QualifyingCell
                                qualifying={c.qualifying}
                                onOpen={
                                  typeof openQualifyingSessionForCandidate === 'function'
                                    ? () => openQualifyingSessionForCandidate(c)
                                    : undefined
                                }
                              />
                            </QualifyingAgentBoundary>
                          </td>
                        ) : null;
                      case 'applicationForm':
                        return resolvedShowApplicationFormColumn ? (
                          <td key={colId} className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell" onClick={(e) => e.stopPropagation()}>
                            <ApplicationFormCell candidate={c} screeningId={screeningId || screeningMeta?.id} />
                          </td>
                        ) : null;
                      case 'transcriptsAvailable': {
                        if (!resolvedShowTranscriptsColumn) return null;
                        const answer = getTranscriptsAvailableLabel(c);
                        return (
                          <td key={colId} className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${answer === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                              {answer}
                            </span>
                          </td>
                        );
                      }
                      default:
                        return null;
                    }
                  })}

                  {showCandidateDetailsColumn && (
                    <td
                      className="px-4 py-3 whitespace-nowrap text-sm text-center table-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        data-testid="view-candidate-btn"
                        disabled={c.isAllZero}
                        onClick={() => !c.isAllZero && openCandidateResumeBreakdown(c, 'candidate')}
                        className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium border mx-auto ${
                          c.isAllZero
                            ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                            : 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border-indigo-100'
                        }`}
                        title={c.isAllZero ? 'No screening data available to view' : 'View candidate details'}
                      >
                        <span>View</span>
                        <EyeIcon className="h-4 w-4 shrink-0" />
                      </button>
                    </td>
                  )}


                  {!hideRowActions && (
                    <td className="px-4 py-3 whitespace-nowrap text-center sticky right-0 z-10" style={{ boxShadow: '-3px 0 6px -3px rgba(0,0,0,0.15)', backgroundColor: '#fff' }} onClick={(e) => e.stopPropagation()}>

                      <div className="relative">

                        <button

                          type="button"
                          data-testid="candidate-actions-dropdown"
                          data-candidate-actions-trigger

                          onClick={(e) => {

                            e.stopPropagation();

                            const candidateId = c.id || c._id;

                            if (

                              openActionsCandidate &&

                              (openActionsCandidate.id === candidateId ||

                                openActionsCandidate._id === candidateId)

                            ) {

                              setOpenActionsCandidate(null);

                              setActionMenuPosition(null);

                            } else {

                              const rect = e.currentTarget.getBoundingClientRect();

                              const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;

                              const rightOffset = Math.max(8, viewportWidth - rect.right - 8);

                              setActionMenuPosition({

                                top: rect.top,

                                right: rightOffset,

                              });

                              setOpenActionsCandidate(c);

                            }

                          }}

                          className="text-gray-400 hover:text-gray-700 inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors"

                          title="Actions"

                        >

                          <EllipsisVerticalIcon className="h-5 w-5" />

                        </button>

                        {openActionsCandidate &&

                          actionMenuPosition &&

                          (openActionsCandidate.id === candidateId ||

                            openActionsCandidate._id === candidateId) &&

                          createPortal(

                            <div

                              data-candidate-actions-menu

                              className="fixed min-w-[110px] bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden py-1"

                              style={{

                                top: `${actionMenuPosition.top}px`,

                                right: `${actionMenuPosition.right}px`,

                                transform: 'translateY(calc(-100% - 8px))',

                              }}

                              onClick={(e) => e.stopPropagation()}

                            >

                              {isDroppedView ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleRestoreFromDroppedRow(openActionsCandidate);
                                      setOpenActionsCandidate(null);
                                      setActionMenuPosition(null);
                                    }}
                                    data-testid="action-restore-dropped"
                                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                  >
                                    <ArrowUturnLeftIcon className="h-4 w-4 text-green-600" />
                                    Restore
                                  </button>
                                </>
                              ) : isRejectedView ? (

                                <>

                                  <button

                                    type="button"

                                    onClick={() => {

                                      handleRestoreFromRejectedRow(openActionsCandidate);

                                      setOpenActionsCandidate(null);

                                      setActionMenuPosition(null);

                                    }}
                                    data-testid="action-restore"
                                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                  >
                                    <ArrowUturnLeftIcon className="h-4 w-4 text-green-600" />
                                    Restore

                                  </button>

                                </>

                              ) : isShortlistView && openActionsCandidate.shortlisted ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleRemoveFromShortlistRow(openActionsCandidate);
                                    setOpenActionsCandidate(null);
                                    setActionMenuPosition(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                                >
                                  <TrashIcon className="h-4 w-4 mr-3" />
                                  Remove
                                </button>
                              ) : (

                                <>

                                  {!openActionsCandidate.shortlisted && (

                                    <button

                                      type="button"
                                      data-testid="action-select"
                                      onClick={() => {
                                        setCandidateToShortlist(openActionsCandidate);
                                        setShowShortlistConfirmModal(true);
                                        setOpenActionsCandidate(null);
                                        setActionMenuPosition(null);
                                      }}
                                      className="flex items-center w-full px-4 py-2.5 text-sm text-[#007BFF] hover:bg-blue-50"
                                    >
                                      <StarIcon className="h-4 w-4 mr-3" />
                                      {isSamtaGroup ? 'Shortlist' : 'Select'}
                                    </button>
                                  )}

                                  {!openActionsCandidate.rejected && (
                                    <button
                                      type="button"
                                      data-testid="action-reject"
                                      onClick={() => {
                                        setCandidateToReject(openActionsCandidate);
                                        setShowRejectConfirmModal(true);
                                        setOpenActionsCandidate(null);
                                        setActionMenuPosition(null);
                                      }}
                                      className="flex items-center w-full px-4 py-2.5 text-sm text-orange-600 hover:bg-orange-50 transition-colors"
                                    >
                                      <XMarkIcon className="h-4 w-4 mr-3" />
                                      Reject
                                    </button>
                                  )}

                                  {isGoColors && !openActionsCandidate.dropped && (
                                    <button
                                      type="button"
                                      data-testid="action-drop"
                                      onClick={() => {
                                        if (typeof handleMarkDropped === 'function') {
                                          handleMarkDropped(openActionsCandidate._id || openActionsCandidate.id, openActionsCandidate.name || 'Candidate');
                                        }
                                        setOpenActionsCandidate(null);
                                        setActionMenuPosition(null);
                                      }}
                                      className="flex items-center w-full px-4 py-2.5 text-sm text-purple-600 hover:bg-purple-50 transition-colors"
                                    >
                                      <XMarkIcon className="h-4 w-4 mr-3" />
                                      Mark as Dropped
                                    </button>
                                  )}

                                  {isGoColors && !openActionsCandidate.movedFrom && (
                                    <button
                                      type="button"
                                      data-testid="action-move-job-post"
                                      onClick={() => {
                                        if (typeof onMoveCandidateJobPost === 'function') {
                                          onMoveCandidateJobPost(openActionsCandidate);
                                        }
                                        setOpenActionsCandidate(null);
                                        setActionMenuPosition(null);
                                      }}
                                      className="flex items-center w-full px-4 py-2.5 text-sm text-teal-700 hover:bg-teal-50 transition-colors"
                                    >
                                      <ArrowRightCircleIcon className="h-4 w-4 mr-3" />
                                      Move to another job post
                                    </button>
                                  )}

                                  {isGoColors && openActionsCandidate.movedFrom && (
                                    <button
                                      type="button"
                                      data-testid="action-undo-move-job-post"
                                      onClick={() => {
                                        if (typeof onUndoMoveCandidateJobPost === 'function') {
                                          onUndoMoveCandidateJobPost(openActionsCandidate);
                                        }
                                        setOpenActionsCandidate(null);
                                        setActionMenuPosition(null);
                                      }}
                                      className="flex items-center w-full px-4 py-2.5 text-sm text-teal-700 hover:bg-teal-50 transition-colors"
                                    >
                                      <ArrowUturnLeftIcon className="h-4 w-4 mr-3" />
                                      Move back to previous job post
                                    </button>
                                  )}

                                  <button
                              type="button"
                              onClick={() => {
                                if (openActionsCandidate.isViewed && typeof onMarkCandidateUnviewed === 'function') {
                                  void onMarkCandidateUnviewed(candidateId);
                                } else if (!openActionsCandidate.isViewed && typeof onMarkCandidateViewed === 'function') {
                                  void onMarkCandidateViewed(candidateId);
                                }
                                setOpenActionsCandidate(null);
                                setActionMenuPosition(null);
                              }}
                              className="flex items-center w-full px-4 py-2.5 text-sm text-indigo-600 hover:bg-indigo-50"
                            >
                              {openActionsCandidate.isViewed ? (
                                <>
                                  <EyeSlashIcon className="h-4 w-4 mr-3" />
                                  Mark as unread
                                </>
                              ) : (
                                <>
                                  <EyeIcon className="h-4 w-4 mr-3" />
                                  Mark as read
                                </>
                              )}
                            </button>

                            {typeof onDownloadCandidateReport === 'function' && (
                              <button
                                type="button"
                                data-testid="action-download-report"
                                onClick={() => {
                                  onDownloadCandidateReport(openActionsCandidate);
                                  setOpenActionsCandidate(null);
                                  setActionMenuPosition(null);
                                }}
                                className="flex items-center w-full px-4 py-2.5 text-sm text-indigo-700 hover:bg-indigo-50"
                              >
                                <DocumentTextIcon className="h-4 w-4 mr-3" />
                                Download Report
                              </button>
                            )}

                            {typeof onReviewCandidateWithHiringManager === 'function' && (
                              <button
                                type="button"
                                data-testid="action-hiring-manager-review"
                                onClick={() => {
                                  onReviewCandidateWithHiringManager(openActionsCandidate);
                                  setOpenActionsCandidate(null);
                                  setActionMenuPosition(null);
                                }}
                                className="flex items-center w-full px-4 py-2.5 text-sm text-violet-700 hover:bg-violet-50"
                              >
                                <UserIcon className="h-4 w-4 mr-3" />
                                Review with Hiring Manager
                              </button>
                            )}

                            <button

                                    type="button"

                                    onClick={() => {

                                      setCandidateToResend(openActionsCandidate);

                                      setShowResendConfirmModal(true);

                                      setOpenActionsCandidate(null);

                                      setActionMenuPosition(null);

                                    }}

                              className="flex items-center w-full px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50"

                                  >

                                    <EnvelopeIcon className="h-4 w-4 mr-3" />

                                    Resend

                                  </button>

                                  {typeof onRecallQualifyingCall === 'function' && openActionsCandidate.qualifying && (
                                    <button
                                      type="button"
                                      data-testid="action-recall-qualifying"
                                      onClick={() => {
                                        onRecallQualifyingCall(openActionsCandidate);
                                        setOpenActionsCandidate(null);
                                        setActionMenuPosition(null);
                                      }}
                                      className="flex items-center w-full px-4 py-2.5 text-sm text-blue-700 hover:bg-blue-50"
                                    >
                                      <PhoneArrowUpRightIcon className="h-4 w-4 mr-3" />
                                      Recall
                                    </button>
                                  )}

                                  {showTelephonyActions && screeningMeta?.hasInterview && openActionsCandidate.phone && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (typeof onCallCandidate === 'function') {
                                          onCallCandidate(openActionsCandidate);
                                        }
                                        setOpenActionsCandidate(null);
                                        setActionMenuPosition(null);
                                      }}
                                      className="flex items-center w-full px-4 py-2.5 text-sm text-green-700 hover:bg-green-50"
                                    >
                                      <PhoneIcon className="h-4 w-4 mr-3" />
                                      Call via AI
                                    </button>
                                  )}


                                  <button

                                    type="button"

                                    onClick={() => {

                                      setCandidateToDelete(openActionsCandidate);

                                      setOpenActionsCandidate(null);

                                      setActionMenuPosition(null);

                                    }}

                                    data-testid="action-delete"
                                    className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"

                                  >

                                    <TrashIcon className="h-4 w-4 mr-3" />

                                    Delete

                                  </button>

                                </>

                              )}

                            </div>,

                            document.body

                          )}

                      </div>

                    </td>
                  )}

                </tr>

              );

            })}

          </tbody>

        </table>

        {((typeof totalCandidatesProp === 'number' && totalCandidatesProp > 50) || rows.length > 50 || hasMoreProp) && (
          <div className="flex items-center justify-between px-6 py-3.5 border-t border-gray-200 bg-gray-50/80 rounded-b-xl text-sm mt-1">
            <div className="text-gray-600 font-medium">
              Showing <span className="font-semibold text-gray-900">{onLoadMoreProp ? rows.length : Math.min(displayCount, rows.length)}</span> of <span className="font-semibold text-gray-900">{typeof totalCandidatesProp === 'number' && totalCandidatesProp > 0 ? totalCandidatesProp : rows.length}</span> candidates
            </div>
            {(typeof hasMoreProp === 'boolean' ? hasMoreProp : displayCount < rows.length) && (
              <button
                type="button"
                onClick={onLoadMoreProp || (() => setDisplayCount((prev) => prev + 50))}
                disabled={isLoadingMoreProp}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-[#007BFF] hover:bg-[#0056b3] disabled:opacity-60 rounded-lg transition-all shadow-sm active:scale-95 cursor-pointer"
              >
                <span>{isLoadingMoreProp ? 'Loading...' : 'Load More'}</span>
                <ChevronDownIcon className="h-4 w-4 text-white" />
              </button>
            )}
          </div>
        )}

      </div>

      {duplicateModalCandidate && (
        <AnimatedModal
          isOpen={!!duplicateModalCandidate}
          onClose={() => setDuplicateModalCandidate(null)}
          ariaLabelledBy="duplicate-history-title"
          contentClassName="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-100"
          zIndex={70}
        >
          <div className="flex flex-col max-h-[85vh]">
            <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
              <div>
                <h2 id="duplicate-history-title" className="text-base font-semibold text-gray-900">
                  Previous appearances
                </h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  {duplicateModalCandidate?.duplicateSummary?.count || 0} screening
                  {duplicateModalCandidate?.duplicateSummary?.count === 1 ? '' : 's'} for
                  {' '}
                  {resolveCandidateDisplayName(duplicateModalCandidate) || duplicateModalCandidate?.name || 'Candidate'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDuplicateModalCandidate(null)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close duplicate history"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-4">
              {(!duplicateModalCandidate?.duplicateSummary?.screenings || duplicateModalCandidate.duplicateSummary.screenings.length === 0) ? (
                <p className="text-sm text-gray-500">No other screenings recorded yet.</p>
              ) : (
                <ul className="grid grid-cols-1 gap-4">
                  {duplicateModalCandidate.duplicateSummary.screenings.map((entry) => (
                    <li key={`${entry.screeningId}-${entry.jobId}-${entry.completedAt || entry.status}`} className="rounded-xl border border-gray-200 bg-white shadow-sm p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-900 truncate">
                            Screening Title: <span className='font-semibold'>{entry.screeningName || 'Screening'}</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">
                            Job Title: {entry.jobTitle || 'Job'}
                          </p>
                        </div>
                        {entry.jobId && entry.screeningId && !isPublicView && (
                          <Link
                            to={`/dashboard/job-posts/jobs/${entry.jobId}/screenings/${entry.screeningId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-[#007BFF]/60 hover:text-[#007BFF] shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                          </Link>
                        )}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs">
                        {entry.resumeScore != null && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-gray-500">Resume:</span>
                            <span className="font-semibold text-gray-800">{entry.resumeScore}%</span>
                          </div>
                        )}
                        {entry.assessmentScore != null && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-gray-500">Assessment:</span>
                            <span className="font-semibold text-gray-800">{entry.assessmentScore}%</span>
                          </div>
                        )}
                        {entry.interviewScore != null && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-gray-500">Interview:</span>
                            <span className="font-semibold text-gray-800">{entry.interviewScore}%</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-500">Completion date:</span>
                          <span className="font-semibold text-gray-800">{entry.completedAt ? new Date(entry.completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : '—'}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </AnimatedModal>
      )}

      {openNotesModalId && createPortal(
        <CandidateNotesModal
          isOpen={!!openNotesModalId}
          onClose={handleNotesModalClose}
          candidateId={openNotesModalId}
          candidateName={openNotesModalName}
          screeningId={screeningId}
          shareCode={notesShareCode}
          reviewerName={notesReviewerName}
          currentUserId={user?.id || user?._id || null}
          currentUserName={user?.name || user?.fullName || user?.email || ''}
          onNoteAdded={handleNoteAdded}
        />,
        document.body
      )}

    </>
  );
}
